import { EventEmitter, Injectable } from '@angular/core';
import { HTTP } from '@ionic-native/http/ngx';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AppEndUserOwnerInfo } from './models/end-user-owner-info.model';
import {
  CHECKHEAD,
  DocumentItem,
  LastShiftTotalsResponse,
  ShiftItem,
  TaxDocumentType,
  TaxError,
  TaxErrorEnum,
  TaxObject,
  TransactionsRegistrarStateResponse,
  isOfflineSequenceActive,
} from './models/tax.model';
import {
  DEFAULT_CASHIER_NAME,
  DocumentIssuer,
  TaxDocumentUtility,
  createDocumentHead,
  extractTicketXml,
  formatOrderDate,
  formatOrderTime,
  getTaxTicketError,
  hashTaxDocument,
  isDocumentRejectedByServer,
  isPreviousDocumentHashRequired,
  parseOrderDateTime,
  parseTaxTicket,
  parseXml,
} from 'tax-register-core';
import { sha256 } from 'js-sha256';
import { getCmsSignedContent } from './services/cms-content';
import {
  AgentSigner,
  IPrivateKeySettings,
} from './services/agent-signer.service';
import { xml2json } from 'src/app/utility/xml-utils';
import { ITaxOrder, TaxOrder, TaxTicket } from './models/tax-order.model';
import { PrivateKeyService } from './services/private-key.service';
import { SettingsService } from 'src/app/services/settings.service';
import { ToastController } from '@ionic/angular';
import { TaxApiService } from './services/tax-api.service';
import {
  OfflinePackageResponse,
  prepareOfflinePackage,
  readOfflinePackageResponse,
} from 'tax-register-core';
import { SharedService } from 'src/app/services/shared.service';
import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  ITaxDocument,
  TaxRegisterService,
} from './services/tax-register.service';
import { ITaxConfig } from './services/tax-register-config.service';
import { TaxRegisterConfigAccessService } from './services/tax-register-config-access.service';
import { UUID } from 'angular2-uuid';
import { IReceipt, Receipt } from 'src/app/private/terminal/terminal.models';
import {
  ITaxRegisterLogger,
  MonitoringService,
} from 'src/app/services/monitoring.service';
import { ReturnOrderModel } from 'src/app/models/return-order.model';
import { PaymentForm } from 'src/app/models/payment-form.enum';
import { TaxRegisterV2Service } from './services/tax-register-v2.service';
import {
  OfflineSessionState,
  TaxRegisterContext,
  TaxRegisterContextService,
  TaxServerState,
} from './services/tax-register-context.service';
import { TaxRegisterWorker } from './services/tax-register.worker';
import {
  DocumentTime,
  RECOVERY_OFFLINE_SESSION,
  RecoveryIssuer,
  RecoveryPosition,
  RecoverySequence,
  clampDocumentTime,
  getRecoveryIssuer,
  isFinancialDocument,
  needsConversion,
  needsIssuerUpdate,
  needsRecoveryMode,
  needsTimeUpdate,
  planRecoveryRebuild,
  readDocumentTime,
  recomputeOfflineNumber,
  renumberAtPosition,
  replaceDocumentIssuer,
  selectRecoveryDocuments,
} from './services/tax-recovery';
import {
  buildFakePrroState,
  createFakePrroCredentials,
  isFakePrro,
  planRealDocumentDelivery,
  readDocumentIdentity,
} from './services/fake-prro';
import { FakePrroStore } from './services/fake-prro-store.service';
import { PrroLogService } from './services/prro-log.service';
import { TaxRegisterBlockService } from './services/tax-register-block.service';

const PAYMENT_TYPE_CARD = 'card';
const PAYMENT_TYPE_CASH = 'cash';
const PAYMENT_TYPE_BONUSES = 'bonuses';
const PAYMENT_TYPE_AFTER_PAYMENT = 'afterPayment';
const START_OFFLINE_SESSION = 102;
// DocumentValidationError (9) of an online document while the server has the register's offline
// session open.
const OFFLINE_SESSION_BLOCKS_ONLINE =
  'Реєстрація документів в режимі онлайн неможлива до завершення офлайн сесії';
const HTTP_NO_CONTENT = 204;
// API ErrorCode 13 NoContent: "Запитані дані відсутні".
const TAX_ERROR_NO_CONTENT = 13;
// Kept in the register's offline errors when a fake context's document goes offline.
const FAKE_PRRO_OFFLINE_CAUSE =
  'fakePrro: каса працює без даних сервера податкової, документи зберігаються офлайн.';

// The server has begun an offline session that holds no check or Z-report, and refuses to end
// such a session ("Пакет порожній").
const EMPTY_SERVER_SESSION_ERROR =
  'Офлайн сесія на сервері податкової не містить жодного чека чи Z-звіту, тому сервер не прийме її завершення ("Пакет порожній"). Сесію буде завершено після наступного чека.';

// Documents were saved in a session the server had already closed (the answer to its end was
// lost), so they can not be sent in it.
const SERVER_SESSION_ENDED_ERROR =
  'Сервер уже завершив офлайн сесію, а після її останнього документа створено нові документи. Потрібне ручне втручання.';

interface IAgentSignerError {
  code: string;
  message: string;
}

interface ITaxDocumentProcessResult {
  success: boolean;
  ticket?: TaxTicket;
  response?: string;
  ticketXml?: string;
  signerError?: IAgentSignerError;
  taxError?: string;
  ticketError?: string;
  status?: string;
}

export type SignerState = 'off' | 'pending' | 'keyReady' | 'ready' | 'error';

// Where a recovery upload is on the server.
interface RecoveryServer {
  state: TransactionsRegistrarStateResponse;
  position: RecoveryPosition;
  // The server's document before its open session (its last document when none is open), and
  // its last document; the second becomes the last document sent.
  documents: string[];
}

interface RecoveryUpload {
  server: RecoveryServer | null;
  // Documents still to send in this pass, in order.
  documents: string[];
}

const LOGGER_NAGE = 'TaxRegister';

@Injectable({ providedIn: 'root' })
export class TaxService {
  selectedStore = new BehaviorSubject<any>(null);
  selectedPrro = new BehaviorSubject<any>(null);
  onTaxRegisterReady = new EventEmitter<boolean>();
  shiftStateChanged = new BehaviorSubject<LastShiftTotalsResponse>(null);
  private _pendingTaxApiConnectErrorEmulation = new Set<string>();
  private _activeTaxApiConnectErrorEmulation = new Set<string>();

  _checkTaxOrderInterval: Observable<number>;
  _checkTaxOrderSub: Subscription;
  OnConfigurePrivateKey = new EventEmitter<{
    privateKeyBase64: string;
    privateKeyName: string;
    password: string;
    providerAddress: string;
    providerCmpAddress: string;
    providerTspAddress: string;
    providerTspAddressPort: string;
  }>();

  _webHttp: HttpClient;
  readonly signerState = new BehaviorSubject<SignerState>('off');
  private _registerExclusiveTasks = new Map<string, Promise<unknown>>();
  // FORCE_ONLINE_MODE: documents already sent again after the server's offline session was ended.
  private _offlineEndRetried = new WeakSet<ITaxOrder>();
  // Recovery uploads in progress, per register.
  private _recoveryUploads = new Map<string, Promise<void>>();
  private _taxLogger: ITaxRegisterLogger;

  constructor(
    private agentSigner: AgentSigner,
    private privateKeyService: PrivateKeyService,
    private _settingsService: SettingsService,
    private _taxApi: TaxApiService,
    private http: HTTP,
    private _sharedService: SharedService,
    private _httpBackEnd: HttpBackend,
    private taxRegisterService: TaxRegisterService,
    private toastCtrl: ToastController,
    private taxRegisterConfigService: TaxRegisterConfigAccessService,
    private ms: MonitoringService,
    private taxRegisterV2Service: TaxRegisterV2Service,
    private taxRegisterContextService: TaxRegisterContextService,
    private taxRegisterWorker: TaxRegisterWorker,
    private _fakePrro: FakePrroStore,
    prroLog: PrroLogService,
    private _blocks: TaxRegisterBlockService,
  ) {
    this._webHttp = new HttpClient(this._httpBackEnd);
    this._taxLogger = this.ms.getTaxLogger();
    prroLog.start((fiscalNumber) =>
      this.taxRegisterConfigService.getRegisterConfig(fiscalNumber),
    );
  }

  // =========================== new code ====================

  getSignerType() {
    return this.agentSigner.getSignerType();
  }

  async launchTaxConfigsSigner() {
    if (this._settingsService.useTaxRegisterContext) {
      return this.launchTaxRegisterContextSigners();
    }

    this._taxLogger.logTrace('launchTaxConfigsSigner');
    const configs = await this.taxRegisterConfigService.getConfig();
    const activeConfigs = configs.filter((c) => c.active);

    if (!activeConfigs.length) {
      return;
    }

    this._sharedService.notify('Завантаження бібліотеки', 10);

    const isInitialized = await this.agentSigner.initialize().catch();
    if (!isInitialized) {
      this._sharedService.showErrorToast(
        'Помилка ініціалізації бібліотеки підписів',
        6,
      );
      return;
    }

    this._sharedService.notify('Завантаження підписів', 4);
    this._msgs = [];
    for await (let conf of activeConfigs) {
      await this.startTaxRegister(conf).catch((err) => {
        this._sharedService.showErrorToast(err, 6);
      });
    }

    if (this._msgs.length) {
      this._sharedService.infoDialogMsg('Попередження', this._msgs).then(() => {
        this._msgs = [];
      });
    }

    this._sharedService.notify('Підписи налаштовано', 3);
    this._sharedService.closeNotify(5);
  }

  async launchTaxRegisterContextSigners(): Promise<void> {
    this._taxLogger.logTrace('launchTaxRegisterContextSigners');
    const configs = await this.taxRegisterContextService.getAllTaxConfigs();
    const readyConfigs = configs.filter(
      (config) =>
        !!config?.cashier?.subjectKeyId &&
        !!config.cashier.privateKey?.keyFileBase64,
    );
    if (!readyConfigs.length) {
      const storePrroConfigs = await this.taxRegisterConfigService
        .getPrroConfigService()
        .getActiveConfigs();
      await this.initStorePrroContexts(storePrroConfigs);
      return;
    }

    this._sharedService.notify('Завантаження бібліотеки', 10);
    const isInitialized = await this.agentSigner
      .initialize()
      .catch(() => false);
    if (!isInitialized) {
      this._sharedService.showErrorToast(
        'Помилка ініціалізації бібліотеки підписів',
        6,
      );
      return;
    }

    this._sharedService.notify('Завантаження підписів', 4);
    this._msgs = [];
    for (const config of readyConfigs) {
      this.signerState.next('pending');
      try {
        await this._launchPrivateKey(config);
        this.signerState.next('ready');
      } catch (error) {
        this.signerState.next('off');
        this._sharedService.showErrorToast(error?.message || error, 6);
      }
    }

    if (this._msgs.length) {
      this._sharedService.infoDialogMsg('Попередження', this._msgs).then(() => {
        this._msgs = [];
      });
    }

    this._sharedService.notify('Підписи налаштовано', 3);
    this._sharedService.closeNotify(5);
  }

  async initStorePrroContexts(configs: ITaxConfig[]): Promise<void> {
    for (const config of configs) {
      try {
        await this.initStorePrroContext(config);
        this.signerState.next('ready');
      } catch (error) {}
    }
  }

  async initStorePrroContext(config: ITaxConfig): Promise<void> {
    if (
      !config?.cashier?.subjectKeyId ||
      !config.cashier.privateKey?.keyFileBase64 ||
      !config.taxRegister?.fiscalNumber
    ) {
      return;
    }

    this._sharedService.notify('Завантаження бібліотеки', 10);
    const isInitialized = await this.agentSigner
      .initialize()
      .catch(() => false);
    if (!isInitialized) {
      this._sharedService.showErrorToast(
        'Помилка ініціалізації бібліотеки підписів',
        6,
      );
      return;
    }

    this._sharedService.notify('Завантаження підписів', 4);
    this.signerState.next('pending');
    try {
      await this.initTaxRegisterContext(config);
      this.signerState.next('ready');
      this._sharedService.notify('Підписи налаштовано', 3);
    } catch (error) {
      this.signerState.next('off');
      this._sharedService.showErrorToast(error?.message || error, 6);
    } finally {
      this._sharedService.closeNotify(5);
    }
  }

  // ignoreBlock: an admin's own (re)initialization of a register it blocked.
  async initTaxRegisterContext(
    config: ITaxConfig,
    options: { ignoreBlock?: boolean } = {},
  ): Promise<void> {
    if (!config?.cashier || !config?.taxRegister?.fiscalNumber) {
      throw new Error('ПРРО не налаштовано.');
    }

    // Queued with the register's fiscal operations and uploads, so a new snapshot never
    // replaces the journal between a document being sent and being saved.
    const fiscalNumber = String(config.taxRegister.fiscalNumber);
    return this._runRegisterExclusive(
      fiscalNumber,
      () => this._initTaxRegisterContextExclusive(config),
      options,
    ).catch((error) => {
      this._taxLogger.logException(error, 1, {
        operation: 'initTaxRegisterContext',
        taxRegister: fiscalNumber,
      });
      throw error;
    });
  }

  // Support's hard re-initialization. What the app keeps in memory for the register is dropped
  // first: the cached context (the storage may have been edited), the configs, and the cashier
  // key read into the signer, which is then read again. Then a new snapshot from the server, as
  // initTaxRegisterContext. force: also over an offline snapshot (see _initTaxRegisterContextExclusive).
  async hardReinitTaxRegisterContext(
    fiscalNumber: string,
    options: { force?: boolean } = {},
  ): Promise<{ configSource: 'config' | 'context'; freedKeys: number }> {
    const id = String(fiscalNumber ?? '').trim();
    if (!id) {
      throw new Error('fiscalNumber is required');
    }

    return this._runRegisterExclusive(
      id,
      async () => {
        this.taxRegisterContextService.forgetCachedContext(id);
        await this.taxRegisterConfigService.reload();

        const stored = await this.taxRegisterContextService
          .getTaxConfig(id)
          .catch((): ITaxConfig => null);
        const fromConfigs =
          await this.taxRegisterConfigService.getRegisterConfig(id);
        const config = fromConfigs || stored;
        if (!config?.cashier || !config.taxRegister?.fiscalNumber) {
          throw new Error(`ПРРО ${id} не налаштовано на терміналі.`);
        }

        // The old cashier's key as well, when the config now has another one.
        const keys = new Set(
          [stored?.cashier?.subjectKeyId, config.cashier.subjectKeyId].filter(
            Boolean,
          ),
        );
        let freedKeys = 0;
        for (const key of keys) {
          await this.agentSigner
            .ctxFreePrivateKey(key)
            .then(() => freedKeys++)
            .catch((error) =>
              console.warn(`[prro] key ${key} not freed`, error),
            );
        }

        // Sales need the key loaded: read before the tax server is asked, so a failed
        // initialization keeps it; when the new config's key can not be read, the old one is.
        try {
          await this._launchPrivateKey(config);
        } catch (error) {
          if (stored?.cashier && stored !== config) {
            await this._launchPrivateKey(stored).catch(() => undefined);
          }
          throw error;
        }
        await this._initTaxRegisterContextExclusive(config, !!options.force);
        this.signerState.next('ready');
        return {
          configSource: fromConfigs
            ? ('config' as const)
            : ('context' as const),
          freedKeys,
        };
      },
      { ignoreBlock: true },
    ).catch((error) => {
      this._taxLogger.logException(error, 1, {
        operation: 'hardReinitTaxRegisterContext',
        taxRegister: id,
      });
      throw error;
    });
  }

  private async _initTaxRegisterContextExclusive(
    config: ITaxConfig,
    force = true,
  ): Promise<void> {
    const fiscalNumber = config.taxRegister.fiscalNumber;
    const contextKey = config.cashier.subjectKeyId;
    await this._launchPrivateKey(config);

    const serverState = await this._taxApi.getServerState();
    const onlineState = await this._taxApi.ctxGetTaxRegisterState(
      contextKey,
      fiscalNumber,
    );
    const lastShiftTotals = await this._getContextLastShiftTotals(
      contextKey,
      fiscalNumber,
    );

    const hasContext =
      await this.taxRegisterContextService.hasContext(fiscalNumber);
    // force: replaces an offline snapshot too; the core still refuses while the journal holds
    // documents the server does not have.
    if (hasContext && !force) {
      const currentState =
        await this.taxRegisterContextService.getState(fiscalNumber);
      const isOffline =
        currentState.Offline ||
        isOfflineSequenceActive(currentState.OfflineNextLocalNum);
      if (isOffline && this._settingsService.forceOnlineMode) {
        await this._resetContextForOnline(fiscalNumber, contextKey, 'init');
      } else if (isOffline) {
        throw new Error(
          'Неможливо ініціалізувати контекст, каса в офлайн режимі.',
        );
      }
    }

    const initialized = await this.taxRegisterContextService.initContext(
      config,
      onlineState,
      lastShiftTotals,
      serverState,
      force,
    );
    if (!initialized) {
      throw new Error('Не вдалося ініціалізувати контекст каси.');
    }

    // A register whose registration is cancelled (Closed) stays disabled.
    if (!hasContext && !onlineState?.Closed) {
      await this.taxRegisterContextService.setDisabled(fiscalNumber, false);
    }
  }

  // The last shift totals, or null when the server has no shift data (HTTP 204, or the
  // NoContent error code). Any other failure is thrown: an open shift must not start from an
  // empty baseline.
  private async _getContextLastShiftTotals(
    contextKey: string,
    taxRegisterFiscalNumber: string,
  ): Promise<LastShiftTotalsResponse | null> {
    try {
      const totals = await this._taxApi.ctxGetLastShiftTotals(
        contextKey,
        taxRegisterFiscalNumber,
      );
      return totals || null;
    } catch (error) {
      if (this._isNoContentError(error)) {
        return null;
      }
      throw error;
    }
  }

  private _isNoContentError(error: any): boolean {
    if (error?.status === HTTP_NO_CONTENT) {
      return true;
    }

    const text = typeof error === 'string' ? error : error?.error;
    return (
      typeof text === 'string' &&
      new TaxError(text).errorNumberCode === TAX_ERROR_NO_CONTENT
    );
  }

  async startTaxRegister(config: ITaxConfig) {
    this._taxLogger.logTrace('_startTaxRegister');
    const contextKey = config.cashier.subjectKeyId;
    this.signerState.next('pending');
    const userOwnerInfo = await this._launchPrivateKey(config).catch((err) => {
      this.signerState.next('off');
      throw err.message || err;
    });

    this._checkPrivateKeyEndDate(userOwnerInfo);

    this.signerState.next('keyReady');

    const serverState = await this._taxApi.getServerState();
    await this.taxRegisterV2Service.saveServerState(
      config.taxRegister.fiscalNumber,
      serverState,
    );

    const taxRegisterState = await this.ctxGetTaxRegisterState(config);
    const lastShiftTotals = await this._taxApi
      .ctxGetLastShiftTotals(contextKey, config.taxRegister.fiscalNumber)
      .catch(() => null);
    if (lastShiftTotals) {
      await this.taxRegisterService.syncShiftTotals(
        config.taxRegister.fiscalNumber,
        lastShiftTotals,
      );
    }
    if (taxRegisterState && taxRegisterState.ShiftState == 1) {
      this.signerState.next('ready');
    }
  }

  private async _launchPrivateKey(
    config: ITaxConfig,
  ): Promise<AppEndUserOwnerInfo> {
    if (!config.cashier) {
      throw 'Cashier not selected';
    }

    const contextKey = config.cashier.subjectKeyId;
    let userOwnerInfo = await this.agentSigner
      .ctxGetUserOwnerInfo(contextKey)
      .catch(() => {
        return null;
      });
    if (userOwnerInfo) {
      return userOwnerInfo;
    }

    // legacy code
    if (!config.cashier.privateKey) {
      config.cashier.privateKey = await this.privateKeyService
        .getPrivateKeyBySubjectId(config.cashier.subjectKeyId)
        .catch(() => null);
    }
    // end legacy code

    const stored = config.cashier.privateKey;
    if (!stored) {
      throw 'Ключ касира не знайдено';
    }

    await this.agentSigner.initialize();
    userOwnerInfo = await this.agentSigner
      .ctxReadPrivateKey(contextKey, {
        cashierId: config.cashier.id,
        privateKeyBase64: stored?.keyFileBase64,
        privateKeyFileName: stored?.keyFileName,
        password: stored?.password,
        providerName: stored?.providerName,
        cmpAddress: stored?.providerCmpAddress,
        tspAddress: stored?.providerTspAddress,
        tspAddressPort: stored?.providerTspAddressPort,
      })
      .catch((err) => {
        const error = err.message || JSON.stringify(err);
        throw `Помилка зчитування ключа. ${error}`;
      });

    this._checkPrivateKeyEndDate(userOwnerInfo);
    return userOwnerInfo;
  }

  private _msgs: string[] = [];
  private _checkPrivateKeyEndDate(userOwnerInfo: any) {
    if (userOwnerInfo.DaysToEnd < 30) {
      this._msgs.push(
        `Термін дії ключа касира <strong>${userOwnerInfo.SubjCN}</strong> спливає за <strong>${userOwnerInfo.DaysToEnd}</strong> днів.`,
      );
    }
  }

  async ctxGetTaxRegisterState(
    config: ITaxConfig,
  ): Promise<TransactionsRegistrarStateResponse> {
    const state = await this._taxApi.ctxGetTaxRegisterState(
      config.cashier.subjectKeyId,
      config.taxRegister.fiscalNumber,
    );

    await this.taxRegisterService.updateTaxRegister(
      config.taxRegister.fiscalNumber,
      state,
    );
    return state;
  }

  // Support's check of a register: reads the cashier's key into the signer and asks the tax API
  // for the register's state. Nothing is saved; the context keeps its own state.
  async readTaxRegisterState(config: ITaxConfig): Promise<{
    owner: AppEndUserOwnerInfo;
    state: TransactionsRegistrarStateResponse;
  }> {
    if (!config?.cashier?.subjectKeyId || !config?.taxRegister?.fiscalNumber) {
      throw new Error(
        'ПРРО не налаштовано: немає касира або фіскального номера.',
      );
    }

    const owner = await this._launchPrivateKey(config);
    const state = await this._taxApi.ctxGetTaxRegisterState(
      config.cashier.subjectKeyId,
      config.taxRegister.fiscalNumber,
    );
    return { owner, state };
  }

  private async _getTaxRegisterState(
    taxRegisterFiscalNumber: string,
  ): Promise<TransactionsRegistrarStateResponse> {
    const hasContext = await this.taxRegisterContextService.hasContext(
      taxRegisterFiscalNumber,
    );
    if (hasContext) {
      return this.taxRegisterContextService.getState(taxRegisterFiscalNumber);
    }

    return this.taxRegisterService.getState(taxRegisterFiscalNumber);
  }

  ctxGetUserOwnerInfo(contextKey: string): Promise<AppEndUserOwnerInfo> {
    return this.agentSigner.ctxGetUserOwnerInfo(contextKey);
  }

  isAgentSignerReady(taxConf: ITaxConfig): Promise<boolean> {
    return this.agentSigner
      .ctxGetUserOwnerInfo(taxConf.cashier.subjectKeyId)
      .then(() => true)
      .catch((err) => false);
  }

  // State of the register that operations of the store use: its context state, or the legacy
  // state without a context. With fakePrro, a register without a context gets its fake context
  // first, as before an operation, so callers that check the shift before paying see the state
  // the payment uses.
  async getStoreTaxRegisterState(
    storeId: number,
  ): Promise<TransactionsRegistrarStateResponse> {
    await this._ensureStoreFakePrroContext(storeId);
    const fiscalNumber = await this._resolveFiscalNumberByStoreId(storeId);
    return this._getTaxRegisterState(fiscalNumber);
  }

  async isShiftOpened(storeId: number): Promise<boolean> {
    let fiscalNumber: string | null = null;
    try {
      fiscalNumber = await this._resolveFiscalNumberByStoreId(storeId);
    } catch {
      return false;
    }

    const taxRegisterState = await this._getTaxRegisterState(
      fiscalNumber,
    ).catch(() => {
      return null;
    });

    return taxRegisterState?.ShiftState == 1;
  }

  async getChecks(from: Date, to: Date): Promise<any[]> {
    const documents: any[] = [];

    // const response = await this.getShifts(
    //   this.taxRegisterFiscalNumber,
    //   from,
    //   to
    // ).catch((err: TaxError) => {
    //   return [];
    // });

    return documents;
  }

  async stornLastDocument(documentNumder: string, storeId: number) {
    const order = new TaxOrder();
    order.documentType = 0;
    order.documentSubType = 5;
    order.ORDERSTORNUM = documentNumder;

    const result = await this._executeOrderTask(order, storeId);
    return result;
  }

  async startShift(storeId: number, testing = false) {
    const taxOrder = new TaxOrder();
    taxOrder.documentType = 100;
    taxOrder.testing = testing;

    const result = await this._executeOrderTask(taxOrder, storeId);
    return result;
  }

  async stopShift(storeId: number) {
    const taxOrder = new TaxOrder();
    taxOrder.documentType = 101;

    const result = await this._executeOrderTask(taxOrder, storeId);
    return result;
  }

  async sendZRepo(storeId: number) {
    const taxOrder = new TaxOrder();
    await this._ensureStoreFakePrroContext(storeId);
    const fiscalNumber = await this._resolveFiscalNumberByStoreId(storeId);
    const hasContext =
      await this.taxRegisterContextService.hasContext(fiscalNumber);
    if (hasContext) {
      const currentTotals =
        await this.taxRegisterContextService.getLastTotals(fiscalNumber);
      taxOrder.shiftTotals = currentTotals?.Totals;
      return this._executeOrderTask(taxOrder, storeId);
    }

    const conf = await this.taxRegisterConfigService.getStoreConfig(storeId);
    if (!conf?.cashier?.subjectKeyId || !conf?.taxRegister?.fiscalNumber) {
      throw new Error('ПРРО для закладу не налаштовано.');
    }

    const lastTotals = await this._taxApi.ctxGetLastShiftTotals(
      conf.cashier.subjectKeyId,
      conf.taxRegister.fiscalNumber,
    );
    taxOrder.shiftTotals = lastTotals?.Totals;
    return this._executeOrderTask(taxOrder, storeId);
  }

  async serviceInputNext(amount: number, storeId: number): Promise<ITaxOrder> {
    const taxOrder = new TaxOrder();
    const _serviceInputDocSubType = 2;
    taxOrder.documentType = 0;
    taxOrder.documentSubType = _serviceInputDocSubType;
    taxOrder.CHECKTOTAL = amount;

    const result = await this._executeOrderTask(taxOrder, storeId);
    this._taxLogger.logTrace('[serviceInputNext] _executeOrderTask');
    result.ORDERTAXNUM = taxOrder.ORDERTAXNUM;
    return result;
  }

  async serviceOutputNext(amount: number, storeId: number): Promise<ITaxOrder> {
    const taxOrder = new TaxOrder();
    const _serviceInputDocSubType = 4;
    taxOrder.documentType = 0;
    taxOrder.documentSubType = _serviceInputDocSubType;
    taxOrder.CHECKTOTAL = amount;

    const result = await this._executeOrderTask(taxOrder, storeId);
    result.ORDERTAXNUM = taxOrder.ORDERTAXNUM;
    return result;
  }

  async _startOfflineSession(taxRegisterFiscalNumber: string) {
    const checkHead = await this._getCheckHead(taxRegisterFiscalNumber, 102);
    const startOfflineShiftDoc = TaxDocumentUtility.getChekDoc(checkHead);
    return this.taxRegisterService.saveDocument(
      taxRegisterFiscalNumber,
      startOfflineShiftDoc,
    );
  }

  async stopOfflineShift(ctxKey: string, taxRegisterFiscalNumber: string) {
    await this._taxApi.getServerState().catch((err) => {
      throw `Сервер податкової недоступний. ${JSON.stringify(err)}`;
    });

    const onlineState = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNumber,
    );

    if (!isOfflineSequenceActive(onlineState.OfflineNextLocalNum)) {
      await this.taxRegisterService.revokeLastOnline(
        taxRegisterFiscalNumber,
        onlineState.NextLocalNum,
      );

      await this._stopOfflineSession(taxRegisterFiscalNumber).catch((err) => {
        const taxError = new TaxError(err);
        if (taxError.error == TaxErrorEnum.OfflineSessionNotOpened) {
          return;
        }

        throw err;
      });
    }

    const data = await this._sendOfflinePackage(
      ctxKey,
      taxRegisterFiscalNumber,
    );
    if (!data) {
      throw 'Помилка відправки офлайн пакета';
    }

    await this.taxRegisterService.finishOffline(taxRegisterFiscalNumber);
    await this.syncLastShiftTotals(ctxKey, taxRegisterFiscalNumber);
    await this.syncTaxRegisterState(ctxKey, taxRegisterFiscalNumber);
  }

  async syncLastShiftTotals(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    const totals = await this._taxApi.ctxGetLastShiftTotals(
      ctxKey,
      taxRegisterFiscalNumber,
    );
    return this.taxRegisterService.syncShiftTotals(
      taxRegisterFiscalNumber,
      totals,
    );
  }

  async syncTaxRegisterState(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
  ): Promise<TransactionsRegistrarStateResponse> {
    const state = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNumber,
    );

    await this.taxRegisterService.updateTaxRegister(
      taxRegisterFiscalNumber,
      state,
    );
    if (isOfflineSequenceActive(state.OfflineNextLocalNum)) {
      if (state.ZRepPresent && state.ShiftState == 1) {
        const lastZRep = await this._taxApi.ctxGetZRepXml(
          ctxKey,
          taxRegisterFiscalNumber,
          state.LastFiscalNum,
        );
        await this.taxRegisterService.saveOnlineDoc(
          taxRegisterFiscalNumber,
          lastZRep,
        );
      } else {
        const lastCheck = await this._taxApi.ctxGetCheckXml(
          ctxKey,
          taxRegisterFiscalNumber,
          state.LastFiscalNum,
        );
        await this.taxRegisterService.saveOnlineDoc(
          taxRegisterFiscalNumber,
          lastCheck,
        );
      }
    }
    return state;
  }

  private async _stopOfflineSession(taxRegisterFiscalNumber: string) {
    const state = await this._getTaxRegisterState(taxRegisterFiscalNumber);

    const checkHead = await this._getCheckHead(taxRegisterFiscalNumber, 103);
    const stopOfflineShiftDoc = TaxDocumentUtility.getChekDoc(checkHead);
    return this.taxRegisterService.saveDocument(
      taxRegisterFiscalNumber,
      stopOfflineShiftDoc,
    );
  }

  private async _sendOfflinePackage(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
  ): Promise<any> {
    const perts = await this.taxRegisterService.getOfflineParts(
      taxRegisterFiscalNumber,
    );
    if (!perts || !perts.length) {
      throw 'Offline session is empty';
    }

    let _result: any;
    for (let pack of perts) {
      const result = await this._processPackage(ctxKey, pack).catch((err) => {
        this._sharedService.showErrorToast(
          `Помилка підклюсчення до інтернету.`,
          10000,
        );

        this._taxLogger.logTrace('_processPackage error');
        this._taxLogger.logTrace(JSON.stringify(err));
        throw err;
      });

      if (!result.success) {
        throw result.error;
      }

      if (result.data) {
        _result = result.data;
        break;
      }
    }

    if (!_result) {
      throw 'Помилка відправки оффлайн пакета';
    }

    return _result;
  }

  private async _getHeaderTaxOrder(
    taxRegisterFiscalNumber: string,
    docType: number,
    subDocType?: number,
    checkTotal?: string,
  ): Promise<ITaxOrder> {
    const docHead = await this._getCheckHead(
      taxRegisterFiscalNumber,
      docType,
      subDocType,
      checkTotal,
    );

    const taxOrder = new TaxOrder();
    taxOrder.orderNumber = docHead.ORDERNUM;
    taxOrder.ORDERDATE = docHead.ORDERDATE;
    taxOrder.ORDERTIME = docHead.ORDERTIME;
    taxOrder.tin = docHead.TIN;
    taxOrder.ipn = docHead.IPN;
    taxOrder.organizationName = docHead.ORGNM;
    taxOrder.pointName = docHead.POINTNM;
    taxOrder.pointAddress = docHead.POINTADDR;
    taxOrder.cashDeskNum = docHead.CASHDESKNUM;
    taxOrder.cashRegisterNum = docHead.CASHREGISTERNUM;
    taxOrder.cashier = docHead.CASHIER;

    if (docHead.DOCTYPE == 100) {
      // start new shift
    } else {
      taxOrder.testing = docHead.TESTING;
    }

    return taxOrder;
  }

  private async _getCheckHead(
    taxRegisterFiscalNumber: string,
    docType: number,
    subDocType?: number,
    checkTotal?: string,
  ) {
    const taxConfig = await this.taxRegisterConfigService.getRegisterConfig(
      taxRegisterFiscalNumber,
    );
    const state = await this._getTaxRegisterState(taxRegisterFiscalNumber);
    const userOwnerInfo = await this.agentSigner.ctxGetUserOwnerInfo(
      taxConfig.cashier.subjectKeyId,
    );
    if (!userOwnerInfo) {
      throw 'private key error';
    }
    const uid = this._getUuid();

    let serverTime: Date | null = null;
    if (this._settingsService.usePrroServerTimeCorrection) {
      serverTime = await this.taxRegisterV2Service
        .getServerTime(taxRegisterFiscalNumber)
        .catch((err) => {
          console.error(err);
          return new Date();
        });
    }

    if (!serverTime) {
      serverTime = new Date();
    }

    // Legacy flow: the register is offline while the server's offline sequence is active.
    // FORCE_ONLINE_MODE: documents go online whatever the sequence; only the session's own begin
    // and end documents stay offline.
    const ignoreOffline =
      this._settingsService.forceOnlineMode &&
      docType != TaxDocumentType.OFFLINE_END;
    const isOffline =
      (!ignoreOffline && isOfflineSequenceActive(state.OfflineNextLocalNum)) ||
      docType == START_OFFLINE_SESSION;
    return createDocumentHead({
      docType,
      docSubType: subDocType,
      uid,
      orderDate: formatOrderDate(serverTime),
      orderTime: formatOrderTime(serverTime),
      orderNumber: state.NextLocalNum,
      issuer: {
        tin: taxConfig.tin,
        ipn: taxConfig.ipn,
        organizationName: taxConfig.orgFullName,
        pointName: taxConfig.taxRegister.objectName,
        pointAddress: taxConfig.taxRegister.address,
        cashRegisterNum: taxConfig.taxRegister.fiscalNumber,
        cashDeskNum: taxConfig.taxRegister.localNumber,
        cashier: this._getCashierName(userOwnerInfo),
      },
      testing: state.Testing,
      offline: isOffline
        ? {
            sessionId: state.OfflineSessionId,
            seed: state.OfflineSeed,
            localNum: +state.OfflineNextLocalNum,
            previousDocumentHash: isPreviousDocumentHashRequired(
              state.OfflineNextLocalNum,
            )
              ? await this.taxRegisterService.getLastDocHash(
                  taxRegisterFiscalNumber,
                )
              : null,
            checkTotal,
          }
        : null,
    });
  }

  private _getUuid() {
    return UUID.UUID();
  }

  async sendOrder(
    order: IReceipt,
    fiscalData: { key: string; value: string }[],
  ): Promise<ITaxOrder> {
    const _taxOrder = await this._getTaxObject(order, fiscalData);
    order.documentDate = new Date().toISOString();
    // check doogs documentType = 0
    _taxOrder.documentType = 0;
    const result = await this._executeOrderTask(_taxOrder, order.storeId);

    return result;
  }

  async getTaxObject(order: IReceipt) {
    return this._getTaxObject(order, []);
  }

  private _validateReturnDocument(document: ITaxDocument): void {
    const head = document?.CHECK?.CHECKHEAD;
    const documentSubType =
      head?.DOCSUBTYPE === null || head?.DOCSUBTYPE === undefined
        ? 0
        : Number(head.DOCSUBTYPE);
    if (
      !head ||
      Number(head.DOCTYPE) !== TaxDocumentType.SALE_GOODS ||
      documentSubType !== 0
    ) {
      throw new Error('Повернення можливе лише для чека продажу товарів.');
    }
  }

  // Built from the store's register, the one _executeOrderTask sends it with.
  private async _getTaxObject(
    order: IReceipt,
    fiscalData: { key: string; value: string }[],
  ): Promise<ITaxOrder> {
    const _taxOrder = await this.getStoreTaxOrderObject(order.storeId);
    _taxOrder.CHECKTOTAL = order.total;
    _taxOrder.DISCOUNTSUM = order.discount;
    _taxOrder.PROVIDED = order.providedSum;
    _taxOrder.REMAINS = order.remainsSum;
    _taxOrder.CHECKTAX = order.taxes;
    _taxOrder.RNDSUM = order.rndSum;
    _taxOrder.NORNDSUM = order.noRndSum;

    _taxOrder.CHECKBODY = order.products.map((p) => {
      return {
        NAME: p.name,
        CODE: p.productid,
        PRICE: p.price,
        AMOUNT: p.quantity,
        COST: p.amount,

        // unit: p.unitId,
        LETTERS: (<any>p).letters,
        EXCISELABELS: p.exciseLabels,
        UKTZED: p.uktzedcode,
        DISCOUNTTYPE: p.discountType,
        DISCOUNTPERCENT: p.percentDiscount,
        DISCOUNTSUM: p.amountDiscount,
      };
    });
    Receipt.appendServiceToCheckBody(order, _taxOrder.CHECKBODY);

    const payForm = this._getPaymentForm(order.paymentType);

    if (fiscalData) {
      const paySysName = fiscalData.find((fd) => fd.key == 'ЕКВАЙЕР');
      const RRN = fiscalData.find((fd) => fd.key == 'RRN');

      if (payForm) {
        payForm.PAYSYS = [
          { NAME: paySysName?.value, ACQUIRETRANSID: RRN?.value },
        ];
      }
    }
    _taxOrder.CHECKPAY = [
      {
        PAYFORMCD: payForm.PAYFORMCD,
        PAYFORMNM: payForm.PAYFORMNM,
        PAYSYS: payForm.PAYSYS,
        SUM: order.total,
      },
    ];

    _taxOrder.CHECKTAX = order.taxes || [];
    _taxOrder.uid = order.uuid;

    return _taxOrder;
  }

  private _getPaymentForm(
    paymentType: 'card' | 'cash' | 'bonuses' | 'afterPayment',
  ): {
    PAYFORMCD: number;
    PAYFORMNM: string;
    PAYSYS?: any[];
  } {
    if (paymentType == PAYMENT_TYPE_AFTER_PAYMENT) {
      return { PAYFORMCD: 3, PAYFORMNM: 'БЕЗГОТІВКОВА' };
    }

    if (paymentType == PAYMENT_TYPE_CASH) {
      return { PAYFORMCD: 0, PAYFORMNM: 'ГОТІВКА' };
    }

    if (paymentType == PAYMENT_TYPE_CARD) {
      return { PAYFORMCD: 1, PAYFORMNM: 'БЕЗГОТІВКОВА' };
    }

    if (paymentType == PAYMENT_TYPE_BONUSES) {
      return { PAYFORMCD: 3, PAYFORMNM: 'БЕЗГОТІВКОВА' };
    }
  }

  async sendSaleCheck(
    taxOrder: ITaxOrder,
    storeId: number,
  ): Promise<ITaxOrder> {
    // check doogs documentType = 0
    taxOrder.documentType = 0;
    const result = await this._executeOrderTask(taxOrder, storeId);
    return result;
  }

  async sendReturnCheck2(
    order: ReturnOrderModel,
    taxRegisterFiscalNumber: string,
    paymentType: 'cash' | 'card' | 'other',
  ): Promise<ITaxOrder> {
    const _taxOrder = await this.getRegisterTaxOrderObject(
      taxRegisterFiscalNumber,
    );

    if (!order.xmlCheck) {
      throw 'Order xml not found';
    }

    const taxDocumentObject = this.taxRegisterService.parseXmlDocument(
      order.xmlCheck,
    );
    this._validateReturnDocument(taxDocumentObject);

    _taxOrder.documentType = 0;
    _taxOrder.documentSubType = 1;

    _taxOrder.CHECKTOTAL = taxDocumentObject.CHECK.CHECKTOTAL.SUM;
    _taxOrder.DISCOUNTSUM = taxDocumentObject.CHECK.CHECKTOTAL.DISCOUNTSUM;
    _taxOrder.PROVIDED = taxDocumentObject.CHECK.CHECKTOTAL.PROVIDED;
    _taxOrder.REMAINS = taxDocumentObject.CHECK.CHECKTOTAL.REMAINS;
    _taxOrder.CHECKTAX = taxDocumentObject.CHECK.CHECKTAX;
    _taxOrder.RNDSUM = taxDocumentObject.CHECK.CHECKTOTAL.RNDSUM;
    _taxOrder.NORNDSUM = taxDocumentObject.CHECK.CHECKTOTAL.NORNDSUM;
    _taxOrder.ORDERRETNUM = order.fiscalNumber;

    // _taxOrder.CHECKBODY = taxDocumentObject.CHECK.CHECKBODY;

    if (
      taxDocumentObject.CHECK.CHECKBODY &&
      taxDocumentObject.CHECK.CHECKBODY.ROW
    ) {
      if (taxDocumentObject.CHECK.CHECKBODY.ROW.length) {
        _taxOrder.CHECKBODY = taxDocumentObject.CHECK.CHECKBODY.ROW;
      } else {
        _taxOrder.CHECKBODY = [taxDocumentObject.CHECK.CHECKBODY.ROW];
      }
    }

    // _taxOrder.CHECKBODY = order.products.map((p) => {
    //   let quantity = p.quantity;
    //   quantity = p.quantity;

    //   return {
    //     NAME: p.name,
    //     CODE: p.productId,
    //     PRICE: p.price,
    //     AMOUNT: quantity,
    //     COST: p.totalAmount,

    //     //unit: p.unitId,
    //     LETTERS: (<any>p).letters,
    //     EXCISELABELS: p.exciseLabels,
    //     UKTZED: p.uktzedCode,
    //     DISCOUNTTYPE: p.discountType,
    //     DISCOUNTPERCENT: p.percentDiscount,
    //     DISCOUNTSUM: p.discount,
    //   };
    // });

    // let PAYFORMCD, PAYFORMNM, PAYFORMTYPE;
    // if (paymentType == 'cash') {
    //   PAYFORMCD = 0;
    //   PAYFORMNM = 'Готівка';
    // }

    // if (paymentType == 'card') {
    //   PAYFORMCD = 1;
    //   PAYFORMNM = 'БЕЗГОТІВКОВА';
    //   PAYFORMTYPE = 'Електронний платіжний засіб';
    // }

    // if (paymentType == 'other') {
    //   PAYFORMCD = 1;
    //   PAYFORMNM = 'ІНШЕ';
    //   PAYFORMTYPE = 'жетон';
    // }

    // _taxOrder.CHECKPAY = [
    //   {
    //     PAYFORMCD: PAYFORMCD,
    //     PAYFORMNM: PAYFORMNM,
    //     SUM: order.total,
    //   },
    // ];

    if (
      taxDocumentObject.CHECK.CHECKPAY &&
      taxDocumentObject.CHECK.CHECKPAY.ROW
    ) {
      if (taxDocumentObject.CHECK.CHECKPAY.ROW.length) {
        _taxOrder.CHECKPAY = taxDocumentObject.CHECK.CHECKPAY.ROW;
      } else {
        _taxOrder.CHECKPAY = [taxDocumentObject.CHECK.CHECKPAY.ROW];
      }
    }

    // _taxOrder.CHECKPAY = taxDocumentObject.CHECK.CHECKPAY;

    _taxOrder.CHECKTAX = [];
    if (
      taxDocumentObject.CHECK.CHECKTAX &&
      taxDocumentObject.CHECK.CHECKTAX.ROW
    ) {
      if (taxDocumentObject.CHECK.CHECKTAX.ROW.length) {
        _taxOrder.CHECKTAX = taxDocumentObject.CHECK.CHECKTAX.ROW;
      } else {
        _taxOrder.CHECKTAX = [taxDocumentObject.CHECK.CHECKTAX.ROW];
      }
    }

    const result = await this._executeOrderTask2(
      _taxOrder,
      taxRegisterFiscalNumber,
    );

    return result;
  }

  async sendReturnCheck(
    taxOrder: ITaxOrder,
    storeId: number,
  ): Promise<ITaxOrder> {
    taxOrder.documentType = 0;
    taxOrder.documentSubType = 1;

    const result = await this._executeOrderTask(taxOrder, storeId);
    return result;
  }

  // Return of a sale check the server registered. The sale is read from the server; the return
  // goes through the register's document flow like any other document, so it is numbered,
  // saved in the journal and counted in the totals.
  async returnCheck(
    ctxKey: string,
    taxRegisterFiscalNum: string,
    checkFiscalNumber: string,
    operationDate?: Date,
  ): Promise<ITaxOrder> {
    if (operationDate) {
      throw new Error('Повернення з іншою датою операції не підтримується.');
    }

    const taxOrder = new TaxOrder() as ITaxOrder;
    taxOrder.ORDERRETNUM = checkFiscalNumber;
    taxOrder.documentType = 0;
    taxOrder.documentSubType = 1;

    const saleCheckXml = await this._taxApi.ctxGetCheckXml(
      ctxKey,
      taxRegisterFiscalNum,
      checkFiscalNumber,
    );

    const docObject = xml2json(saleCheckXml) as ITaxDocument;
    this._validateReturnDocument(docObject);
    taxOrder.CHECKTOTAL = docObject.CHECK.CHECKTOTAL.SUM;
    taxOrder.RNDSUM = docObject.CHECK.CHECKTOTAL.RNDSUM;
    taxOrder.NORNDSUM = docObject.CHECK.CHECKTOTAL.NORNDSUM;
    taxOrder.DISCOUNTSUM = docObject.CHECK.CHECKTOTAL.DISCOUNTSUM;

    if (docObject.CHECK.CHECKBODY.ROW.length) {
      taxOrder.CHECKBODY = docObject.CHECK.CHECKBODY.ROW;
    } else {
      taxOrder.CHECKBODY = [docObject.CHECK.CHECKBODY.ROW];
    }

    if (docObject.CHECK.CHECKPAY.ROW.length) {
      taxOrder.CHECKPAY = docObject.CHECK.CHECKPAY.ROW;
    } else {
      taxOrder.CHECKPAY = [docObject.CHECK.CHECKPAY.ROW];
    }

    if (docObject.CHECK.CHECKTAX) {
      if (docObject.CHECK.CHECKTAX.ROW.length) {
        taxOrder.CHECKTAX = docObject.CHECK.CHECKTAX.ROW;
      } else {
        taxOrder.CHECKTAX = [docObject.CHECK.CHECKTAX.ROW];
      }
    }

    return this._executeOrderTask2(taxOrder, taxRegisterFiscalNum);
  }

  async returnCheckXml(storeId: number, checkXml: string) {
    const taxOrder = new TaxOrder() as ITaxOrder;
    taxOrder.documentType = 0;
    taxOrder.documentSubType = 1;

    const docObject = xml2json(checkXml) as ITaxDocument;
    this._validateReturnDocument(docObject);
    taxOrder.ORDERRETNUM = docObject.CHECK.CHECKHEAD.ORDERTAXNUM;
    taxOrder.CHECKTOTAL = docObject.CHECK.CHECKTOTAL.SUM;
    // taxOrder.RNDSUM = docObject.CHECK.CHECKTOTAL.RNDSUM;
    // taxOrder.NORNDSUM = docObject.CHECK.CHECKTOTAL.NORNDSUM;
    taxOrder.DISCOUNTSUM = docObject.CHECK.CHECKTOTAL.DISCOUNTSUM;

    if (docObject.CHECK.CHECKBODY.ROW.length) {
      taxOrder.CHECKBODY = docObject.CHECK.CHECKBODY.ROW;
    } else {
      taxOrder.CHECKBODY = [docObject.CHECK.CHECKBODY.ROW];
    }

    if (docObject.CHECK.CHECKPAY.ROW.length) {
      taxOrder.CHECKPAY = docObject.CHECK.CHECKPAY.ROW;
    } else {
      taxOrder.CHECKPAY = [docObject.CHECK.CHECKPAY.ROW];
    }

    if (docObject.CHECK.CHECKTAX) {
      if (docObject.CHECK.CHECKTAX.ROW.length) {
        taxOrder.CHECKTAX = docObject.CHECK.CHECKTAX.ROW;
      } else {
        taxOrder.CHECKTAX = [docObject.CHECK.CHECKTAX.ROW];
      }
    }

    const result = await this._executeOrderTask(taxOrder, storeId);
    return result;
  }

  async returnCheckXml2(taxRegisterFiscalNum: string, checkXml: string) {
    const taxOrder = new TaxOrder() as ITaxOrder;
    taxOrder.documentType = 0;
    taxOrder.documentSubType = 1;

    const docObject = xml2json(checkXml) as ITaxDocument;
    this._validateReturnDocument(docObject);
    taxOrder.ORDERRETNUM = docObject.CHECK.CHECKHEAD.ORDERTAXNUM;
    taxOrder.CHECKTOTAL = docObject.CHECK.CHECKTOTAL.SUM;
    // taxOrder.RNDSUM = docObject.CHECK.CHECKTOTAL.RNDSUM;
    // taxOrder.NORNDSUM = docObject.CHECK.CHECKTOTAL.NORNDSUM;
    taxOrder.DISCOUNTSUM = docObject.CHECK.CHECKTOTAL.DISCOUNTSUM;

    if (docObject.CHECK.CHECKBODY.ROW.length) {
      taxOrder.CHECKBODY = docObject.CHECK.CHECKBODY.ROW;
    } else {
      taxOrder.CHECKBODY = [docObject.CHECK.CHECKBODY.ROW];
    }

    if (docObject.CHECK.CHECKPAY.ROW.length) {
      taxOrder.CHECKPAY = docObject.CHECK.CHECKPAY.ROW;
    } else {
      taxOrder.CHECKPAY = [docObject.CHECK.CHECKPAY.ROW];
    }

    if (docObject.CHECK.CHECKTAX) {
      if (docObject.CHECK.CHECKTAX.ROW.length) {
        taxOrder.CHECKTAX = docObject.CHECK.CHECKTAX.ROW;
      } else {
        taxOrder.CHECKTAX = [docObject.CHECK.CHECKTAX.ROW];
      }
    }

    const result = await this._executeOrderTask2(
      taxOrder,
      taxRegisterFiscalNum,
    );
    return result;
  }

  private async _executeOrderTask2(
    taxOrder: ITaxOrder,
    taxRegisterFiscalNumber: string,
  ): Promise<ITaxOrder> {
    //await this._ensureRegisterFakePrroContext(taxRegisterFiscalNumber);
    const cnf = await this.taxRegisterConfigService.getRegisterConfig(
      taxRegisterFiscalNumber,
    );

    const hasContext = await this.taxRegisterContextService.hasContext(
      taxRegisterFiscalNumber,
    );
    if (hasContext) {
      return this._makeFiscalOperation(taxRegisterFiscalNumber, taxOrder);
    }

    // const isConfigValid = await this._validateConfig(cnf).catch((err) => {
    //   this._sharedService.showErrorToast(err);
    //   throw err;
    // });
    const ctxKey = cnf.cashier.subjectKeyId;
    const taxRegisterFiscalNum = cnf.taxRegister.fiscalNumber;

    let docHead = await this._getCheckHead(
      taxRegisterFiscalNum,
      taxOrder.documentType,
      taxOrder.documentSubType,
      taxOrder.CHECKTOTAL?.toFixed(2),
    );

    if (docHead.OFFLINE) {
      await this.stopOfflineShift(ctxKey, taxRegisterFiscalNum)
        .then(async () => {
          this._sharedService.notify(
            `Каса ${taxRegisterFiscalNum} перейшла в онлайн режим`,
          );

          docHead = await this._getCheckHead(
            taxRegisterFiscalNum,
            taxOrder.documentType,
            taxOrder.documentSubType,
            taxOrder.CHECKTOTAL?.toFixed(2),
          );
        })
        .catch(async (err) => {
          this._taxLogger.logException(err, 1, {
            operation: 'stopOfflineShift',
            point: 'catch',
            taxRegister: taxRegisterFiscalNum,
          });
          const offlineDocs =
            await this.taxRegisterService.getOfflineParts(taxRegisterFiscalNum);

          this._taxLogger.logTrace(JSON.stringify(offlineDocs), {
            dataType: 'offlineDocs',
          });
          const _taxError = new TaxError(err);
          if (_taxError && _taxError.error) {
            throw _taxError.text;
          }

          throw err;
        })
        .catch(async (err) => {
          this._taxLogger.logException(err, 1, {
            operation: 'stopOfflineShift',
            point: 'catch2',
            taxRegister: taxRegisterFiscalNum,
          });

          throw err;
        });
    }

    taxOrder.uid = docHead.UID;
    taxOrder.orderNumber = docHead.ORDERNUM;
    taxOrder.ORDERDATE = docHead.ORDERDATE;
    taxOrder.ORDERTIME = docHead.ORDERTIME;
    taxOrder.tin = docHead.TIN;
    taxOrder.ipn = docHead.IPN;
    taxOrder.organizationName = docHead.ORGNM;
    taxOrder.pointName = docHead.POINTNM;
    taxOrder.pointAddress = docHead.POINTADDR;
    taxOrder.cashDeskNum = docHead.CASHDESKNUM;
    taxOrder.cashRegisterNum = docHead.CASHREGISTERNUM;
    taxOrder.cashier = docHead.CASHIER;

    if (docHead.DOCTYPE == 100) {
      // start new shift
    } else {
      taxOrder.testing = docHead.TESTING;
    }

    if (docHead.OFFLINE) {
      throw 'Сервер податкової не доступний';

      // TODO Offline mode
      return this._saveOfflineDoc(taxRegisterFiscalNum, taxOrder, docHead);
    }

    const checkXml = this._getTaxObjectXml(taxOrder);
    taxOrder.xmlDocument = checkXml;

    this._taxLogger.logTrace('xmlDocument', {
      taxRegister: taxRegisterFiscalNum,
    });
    this._taxLogger.logTrace(taxOrder.xmlDocument, {
      taxRegister: taxRegisterFiscalNum,
    });

    const result = await this._processTaxDocument(ctxKey, taxOrder).catch(
      (err) => {
        this._taxLogger.logException(err, 1, {
          operation: '_processTaxDocument',
          taxRegister: taxRegisterFiscalNum,
        });

        throw err;
      },
    );
    if (result.success) {
      taxOrder.ORDERTAXNUM = result.ticket.ORDERTAXNUM;
      taxOrder.xmlDocument = this.taxRegisterService.updateTaxOrderNum(
        taxOrder.xmlDocument,
        result.ticket.ORDERTAXNUM,
      );

      await this.taxRegisterService.saveDocument(
        taxOrder.cashRegisterNum,
        taxOrder.xmlDocument,
      );
      return taxOrder;
    }

    if (!result.success) {
      this._taxLogger.logException(<any>result, 1, {
        operation: '_processTaxDocument',
        point: 'result',
        taxRegister: taxRegisterFiscalNum,
      });
    }

    if (result.taxError) {
      return this._handleTaxError(cnf, taxOrder, result.taxError).catch(
        (error) => {
          this._taxLogger.logException(error, 1, {
            operation: '_handleTaxError',
            point: 'result',
            taxRegister: taxRegisterFiscalNum,
          });

          throw error;
        },
      );
    }

    if (result.signerError) {
      throw result.signerError?.message || result.signerError;
    }

    if (!result.taxError) {
      await this.taxRegisterService.saveDocument(
        taxOrder.cashRegisterNum,
        taxOrder.xmlDocument,
      );
      return this._saveStartOffline(taxRegisterFiscalNum, taxOrder);
    }
  }

  private async _resolveFiscalNumberByStoreId(
    storeId: number,
  ): Promise<string> {
    const contextId =
      await this.taxRegisterContextService.getTaxRegisterIdByStoreId(storeId);
    if (contextId) {
      return contextId;
    }

    const conf = await this.taxRegisterConfigService.getStoreConfig(storeId);
    const fiscalNumber = conf?.taxRegister?.fiscalNumber;
    if (!fiscalNumber) {
      throw new Error('ПРРО для закладу не налаштовано.');
    }

    return String(fiscalNumber);
  }

  private _resolveTaxRegister(
    taxRegister: ITaxConfig['taxRegister'] | null | undefined,
    taxObject?: TaxObject,
    fallbackFiscalNumber?: string | number,
  ): ITaxConfig['taxRegister'] | null {
    if (taxRegister?.fiscalNumber) {
      return taxRegister;
    }

    const registrar = taxObject?.TransactionsRegistrars?.[0];
    const fiscalNumber = registrar?.NumFiscal ?? fallbackFiscalNumber;
    if (fiscalNumber == null || fiscalNumber === '') {
      return null;
    }

    return {
      fiscalNumber: String(fiscalNumber),
      localNumber: registrar?.NumLocal,
      name: registrar?.Name || taxObject?.Name,
      objectName: taxObject?.Name,
      address: taxObject?.Address,
      status: registrar?.Closed ? 'closed' : 'active',
      created: null,
    } as ITaxConfig['taxRegister'];
  }

  private async _executeOrderTask(
    taxOrder: ITaxOrder,
    storeId: number,
  ): Promise<ITaxOrder> {
    //await this._ensureStoreFakePrroContext(storeId, taxOrder.documentType);
    const fiscalNumber = await this._resolveFiscalNumberByStoreId(storeId);
    const hasContext =
      await this.taxRegisterContextService.hasContext(fiscalNumber);
    if (hasContext) {
      return this._makeFiscalOperation(fiscalNumber, taxOrder);
    }

    const cnf = await this.taxRegisterConfigService.getStoreConfig(storeId);
    if (!cnf?.cashier?.subjectKeyId || !cnf?.taxRegister?.fiscalNumber) {
      throw new Error('ПРРО для закладу не налаштовано.');
    }
    const ctxKey = cnf.cashier.subjectKeyId;
    const taxRegisterFiscalNum = cnf.taxRegister.fiscalNumber;

    let docHead = await this._getCheckHead(
      taxRegisterFiscalNum,
      taxOrder.documentType,
      taxOrder.documentSubType,
      taxOrder.CHECKTOTAL?.toFixed(2),
    );

    if (docHead.OFFLINE) {
      await this.stopOfflineShift(ctxKey, taxRegisterFiscalNum)
        .then(async () => {
          this._sharedService.notify(
            `Каса ${taxRegisterFiscalNum} перейшла в онлайн режим`,
          );

          docHead = await this._getCheckHead(
            taxRegisterFiscalNum,
            taxOrder.documentType,
            taxOrder.documentSubType,
            taxOrder.CHECKTOTAL?.toFixed(2),
          );
        })
        .catch(async (err) => {
          this._taxLogger.logException(err, 1, {
            operation: 'stopOfflineShift',
            point: 'catch',
            taxRegister: taxRegisterFiscalNum,
          });
          const offlineDocs =
            await this.taxRegisterService.getOfflineParts(taxRegisterFiscalNum);

          this._taxLogger.logTrace(JSON.stringify(offlineDocs), {
            dataType: 'offlineDocs',
          });
          const _taxError = new TaxError(err);
          if (_taxError && _taxError.error) {
            throw _taxError.text;
          }

          throw err;
        })
        .catch(async (err) => {
          this._taxLogger.logException(err, 1, {
            operation: 'stopOfflineShift',
            point: 'catch2',
            taxRegister: taxRegisterFiscalNum,
          });

          throw err;
        });
    }

    taxOrder.uid = docHead.UID;
    taxOrder.orderNumber = docHead.ORDERNUM;
    taxOrder.ORDERDATE = docHead.ORDERDATE;
    taxOrder.ORDERTIME = docHead.ORDERTIME;
    taxOrder.tin = docHead.TIN;
    taxOrder.ipn = docHead.IPN;
    taxOrder.organizationName = docHead.ORGNM;
    taxOrder.pointName = docHead.POINTNM;
    taxOrder.pointAddress = docHead.POINTADDR;
    taxOrder.cashDeskNum = docHead.CASHDESKNUM;
    taxOrder.cashRegisterNum = docHead.CASHREGISTERNUM;
    taxOrder.cashier = docHead.CASHIER;

    if (docHead.DOCTYPE == 100) {
      // start new shift
    } else {
      taxOrder.testing = docHead.TESTING;
    }

    if (docHead.OFFLINE) {
      throw 'Сервер податкової не доступний';

      // TODO Offline mode
      return this._saveOfflineDoc(taxRegisterFiscalNum, taxOrder, docHead);
    }

    const checkXml = this._getTaxObjectXml(taxOrder);
    taxOrder.xmlDocument = checkXml;

    this._taxLogger.logTrace('xmlDocument', {
      taxRegister: taxRegisterFiscalNum,
    });
    this._taxLogger.logTrace(taxOrder.xmlDocument, {
      taxRegister: taxRegisterFiscalNum,
    });

    const result = await this._processTaxDocument(ctxKey, taxOrder).catch(
      (err) => {
        this._taxLogger.logException(err, 1, {
          operation: '_processTaxDocument',
          taxRegister: taxRegisterFiscalNum,
        });

        throw err;
      },
    );
    if (result.success) {
      taxOrder.ORDERTAXNUM = result.ticket.ORDERTAXNUM;
      taxOrder.xmlDocument = this.taxRegisterService.updateTaxOrderNum(
        taxOrder.xmlDocument,
        result.ticket.ORDERTAXNUM,
      );

      await this.taxRegisterService.saveDocument(
        taxOrder.cashRegisterNum,
        taxOrder.xmlDocument,
      );
      return taxOrder;
    }

    if (!result.success) {
      this._taxLogger.logException(<any>result, 1, {
        operation: '_processTaxDocument',
        point: 'result',
        taxRegister: taxRegisterFiscalNum,
      });
    }

    if (result.taxError) {
      return this._handleTaxError(cnf, taxOrder, result.taxError).catch(
        (error) => {
          this._taxLogger.logException(error, 1, {
            operation: '_handleTaxError',
            point: 'result',
            taxRegister: taxRegisterFiscalNum,
          });

          throw error;
        },
      );
    }

    if (result.signerError) {
      throw result.signerError?.message || result.signerError;
    }

    if (!result.taxError) {
      await this.taxRegisterService.saveDocument(
        taxOrder.cashRegisterNum,
        taxOrder.xmlDocument,
      );
      return this._saveStartOffline(taxRegisterFiscalNum, taxOrder);
    }
  }

  private async _handleTaxError(
    cnf: ITaxConfig,
    taxOrder: ITaxOrder,
    taxError: string,
  ): Promise<any> {
    // process tax errors
    this._taxLogger.logTrace('_handleTaxError', {
      taxRegister: cnf.taxRegister.fiscalNumber,
    });

    const ctxKey = cnf.cashier.subjectKeyId;
    const taxRegisterFiscalNum = cnf.taxRegister.fiscalNumber;

    // FORCE_ONLINE_MODE: the server's offline session blocks the document. The session is ended,
    // the register's state is read again, and the document is sent once more.
    if (
      this._settingsService.forceOnlineMode &&
      this._isOfflineSessionBlockingOnline(taxError) &&
      !this._offlineEndRetried.has(taxOrder)
    ) {
      this._offlineEndRetried.add(taxOrder);
      await this.endOfflineSessionOnline(taxRegisterFiscalNum);
      await this.syncTaxRegisterState(ctxKey, taxRegisterFiscalNum);
      return this._executeOrderTask2(taxOrder, taxRegisterFiscalNum);
    }

    const _taxError = new TaxError(taxError);
    if (_taxError.error == TaxErrorEnum.CheckLocalNumberInvalid) {
      const nextOrderNumber = _taxError.getNextCheckLocalNumberFromError();
      if (nextOrderNumber > 0) {
        taxOrder.orderNumber = nextOrderNumber;
      } else {
        const taxRegister = await this.ctxGetTaxRegisterState(cnf);
        taxOrder.orderNumber = taxRegister.NextLocalNum; //  await this._getNextOrderNumber(true);
      }

      const updatedXml = this._getTaxObjectXml(taxOrder);
      taxOrder.xmlDocument = updatedXml;

      this._taxLogger.logTrace('updatedXmlDocument', {
        taxRegister: cnf.taxRegister.fiscalNumber,
      });
      this._taxLogger.logTrace(taxOrder.xmlDocument, {
        taxRegister: cnf.taxRegister.fiscalNumber,
      });

      const retryResult = await this._processTaxDocument(ctxKey, taxOrder);
      if (retryResult.success) {
        taxOrder.ORDERTAXNUM = retryResult.ticket.ORDERTAXNUM;
        taxOrder.xmlDocument = this.taxRegisterService.updateTaxOrderNum(
          taxOrder.xmlDocument,
          retryResult.ticket.ORDERTAXNUM,
        );
        await this.taxRegisterService.saveDocument(
          taxOrder.cashRegisterNum,
          taxOrder.xmlDocument,
        );
        return taxOrder;
      }

      this._taxLogger.logException(<any>retryResult, 1, {
        operation: '_handleTaxError',
        point: 'process',
        taxRegister: cnf.taxRegister.fiscalNumber,
      });

      if (retryResult.signerError) {
        throw retryResult.signerError?.message || retryResult.signerError;
      }

      if (!taxError) {
        return this._saveStartOffline(taxRegisterFiscalNum, taxOrder);
      }

      const _retryTaxError = new TaxError(retryResult.taxError);
      this._sharedService.showErrorToast(_retryTaxError.text);
      throw retryResult.taxError;
    } else if (_taxError.error == TaxErrorEnum.ShiftAlreadyOpened) {
      this._displayError(_taxError.text);
      throw taxError;
    } else if (_taxError.error == TaxErrorEnum.ZRepAlreadyRegistered) {
      this._displayError(_taxError.text);
      throw taxError;
    } else if (_taxError.error == TaxErrorEnum.DocumentValidationError) {
      this._displayError(_taxError.text);
      throw taxError;
    } else if (_taxError.error == TaxErrorEnum.ShiftNotOpened) {
      this._displayError(_taxError.text);
      throw _taxError;
    }

    throw _taxError.text || 'TaxRegister error';
  }

  private async _saveStartOffline(
    taxRegisterFiscalNum: string,
    taxOrder: ITaxOrder,
  ) {
    const state = await this._getTaxRegisterState(taxRegisterFiscalNum);
    if (!state.OfflineNextLocalNum) {
      throw 'Каса не підтримує оффлайн режим';
    }

    await this._startOfflineSession(taxRegisterFiscalNum);
    this._sharedService.notify(
      `Каса ${taxRegisterFiscalNum} перейшла в оффлайн режим.`,
    );
    const docHead = await this._getCheckHead(
      taxRegisterFiscalNum,
      taxOrder.documentType,
      taxOrder.documentSubType,
      taxOrder.CHECKTOTAL?.toFixed(2),
    );

    return this._saveOfflineDoc(taxRegisterFiscalNum, taxOrder, docHead);
  }

  private async _saveOfflineDoc(
    taxRegisterFiscalNum: string,
    taxOrder: ITaxOrder,
    docHead: CHECKHEAD,
  ) {
    const state = await this._getTaxRegisterState(taxRegisterFiscalNum);
    if (!state.OfflineNextLocalNum) {
      throw 'Каса не підтримує оффлайн режим';
    }

    docHead = await this._getCheckHead(
      taxRegisterFiscalNum,
      taxOrder.documentType,
      taxOrder.documentSubType,
      taxOrder.CHECKTOTAL?.toFixed(2),
    );

    taxOrder.ORDERDATE = docHead.ORDERDATE;
    taxOrder.ORDERTIME = docHead.ORDERTIME;
    taxOrder.orderNumber = docHead.ORDERNUM;
    taxOrder.ORDERTAXNUM = docHead.ORDERTAXNUM;
    taxOrder.PREVDOCHASH = docHead.PREVDOCHASH;
    taxOrder.offline = docHead.OFFLINE;

    const checkXml = this._getTaxObjectXml(taxOrder);
    taxOrder.xmlDocument = checkXml;
    await this.taxRegisterService.saveDocument(taxRegisterFiscalNum, checkXml);

    return taxOrder;
  }

  private async _processTaxDocument(
    ctxKey: string,
    taxOrder: ITaxOrder,
  ): Promise<ITaxDocumentProcessResult> {
    const result: ITaxDocumentProcessResult = {
      success: false,
    };

    const signedData = await this.agentSigner
      .ctxSignData(ctxKey, taxOrder.xmlDocument)
      .catch((err: IAgentSignerError) => {
        this._taxLogger.logException(err, 2, {
          taxRegister: taxOrder.cashRegisterNum,
          errorType: 'SignError',
        });
        result.signerError = err;
        return '';
      });

    if (result.signerError) {
      return result;
    }

    if (this._activeTaxApiConnectErrorEmulation.has(taxOrder.cashRegisterNum)) {
      this._taxLogger.logTrace('Tax API connection error emulation', {
        taxRegister: taxOrder.cashRegisterNum,
      });
      return result;
    }

    const documentResponse = await this._taxApi
      .sendDocumentToTax(signedData)
      .then((response) => {
        if (
          response.success &&
          this._pendingTaxApiConnectErrorEmulation.delete(
            taxOrder.cashRegisterNum,
          )
        ) {
          this._activeTaxApiConnectErrorEmulation.add(taxOrder.cashRegisterNum);
          throw new Error(
            'Emulated tax API connection error after successful document send',
          );
        }
        return response;
      })
      .catch((err) => {
        this._taxLogger.logException(err, 2, {
          taxRegister: taxOrder.cashRegisterNum,
          errorType: 'SendDocumentError',
        });

        return { success: false, httpStatusCode: 0, error: err, data: null };
      });

    if (!documentResponse.success) {
      // 4xx is a server rejection of the document. 5xx means the outcome is unknown and is
      // handled like a connection error (offline fallback), not as a tax validation error.
      if (isDocumentRejectedByServer(documentResponse.httpStatusCode)) {
        result.taxError = documentResponse.error;
      }
      return result;
    }

    result.response = documentResponse.data;
    let ticketXmlStr = extractTicketXml(documentResponse.data);
    if (ticketXmlStr == null) {
      ticketXmlStr = await this.agentSigner
        .verifyData(documentResponse.data as string)
        .catch(() => null);
    }

    if (ticketXmlStr == null) {
      result.ticketError = 'Помилка обробки відповіді серверу ДПС';
      return result;
    }

    const ticketObject = parseTaxTicket(ticketXmlStr) as TaxTicket;
    result.ticketXml = ticketXmlStr;
    result.ticket = ticketObject;

    // A receipt with the fiscal number registered the document whatever its ERRORCODE; without
    // one, a non-zero ERRORCODE is the server's rejection, resolved by the user.
    const ticketError = getTaxTicketError(ticketObject);
    if (ticketError) {
      result.taxError = ticketError;
      return result;
    }

    result.success = true;
    return result;
  }

  private async _processPackage(ctxKey: string, docs: any[]): Promise<any> {
    const signedPac = await this.agentSigner.ctxSignPackage(ctxKey, docs);
    return await this._taxApi.sendPackageToTax(signedPac);
  }

  async readPrivateKey(
    privateKeyConfig: IPrivateKeySettings,
  ): Promise<AppEndUserOwnerInfo> {
    return this.agentSigner
      .readPrivateKey(privateKeyConfig)
      .then((userInfo) => {
        this.signerState.next('keyReady');
        return userInfo;
      })
      .catch((err) => {
        throw err;
      });
  }

  async getAdvancedTaxOrder(
    storeId: number,
    amount: number,
    paymentType: PaymentForm,
    orderId: number,
    productName: string,
  ): Promise<ITaxOrder> {
    const taxOrder = await this.getStoreTaxOrderObject(storeId);

    taxOrder.CHECKTOTAL = amount;
    taxOrder.PROVIDED = amount;

    taxOrder.CHECKBODY = [
      {
        NAME: productName,
        CODE: orderId,
        PRICE: +amount,
        AMOUNT: 1,
        COST: amount,

        LETTERS: '',
        EXCISELABELS: [],
        UKTZED: '',
        DISCOUNTTYPE: null,
        DISCOUNTPERCENT: null,
        DISCOUNTSUM: null,
      },
    ];

    const payForm = this.getPaymentForm(paymentType);
    taxOrder.CHECKPAY = [
      {
        PAYFORMCD: payForm.PAYFORMCD,
        PAYFORMNM: payForm.PAYFORMNM,
        SUM: amount,
      },
    ];

    taxOrder.CHECKTAX = [];

    return taxOrder;
  }

  getPaymentForm(paymentType: PaymentForm): {
    PAYFORMCD: number;
    PAYFORMNM: string;
  } {
    if (paymentType == PaymentForm.cash) {
      return { PAYFORMCD: 0, PAYFORMNM: 'ГОТІВКА' };
    }

    if (paymentType == PaymentForm.card) {
      return { PAYFORMCD: 1, PAYFORMNM: 'БЕЗГОТІВКОВА' };
    }

    if (paymentType == PaymentForm.bonuses) {
      return { PAYFORMCD: 3, PAYFORMNM: 'БЕЗГОТІВКОВА' };
    }
  }

  async getStoreTaxOrderObject(storeId: number): Promise<ITaxOrder> {
    await this._ensureStoreFakePrroContext(storeId);
    const taxContext =
      await this.taxRegisterContextService.getTaxContextByStoreId(storeId);
    if (taxContext) {
      return this._getContextTaxOrderObject(taxContext);
    }

    const conf = await this.taxRegisterConfigService.getStoreConfig(storeId);
    if (!conf?.taxRegister?.fiscalNumber) {
      throw 'Помилка. ПРРО для закладу не налаштовано.';
    }

    const userOwnerInfo = await this.agentSigner.ctxGetUserOwnerInfo(
      conf.cashier.subjectKeyId,
    );

    const taxOrder = new TaxOrder({
      orderNumber: 0,
      tin: conf.tin,
      ipn: conf.ipn || null,
      organizationName: conf.orgFullName,
      pointName: conf.taxRegister.objectName,
      pointAddress: conf.taxRegister.address,
      cashDeskNum: conf.taxRegister.localNumber,
      cashRegisterNum: conf.taxRegister.fiscalNumber,
      cashRegisterName: conf.taxRegister.name,
      cashier: this._getCashierName(userOwnerInfo),
    });

    return taxOrder;
  }

  async getRegisterTaxOrderObject(
    taxRegisterFiscalNumber: string,
  ): Promise<ITaxOrder> {
    await this._ensureRegisterFakePrroContext(taxRegisterFiscalNumber);
    // A register with a context needs neither its config entry nor a loaded key here: the
    // document header is built from the context when the order is sent.
    const taxContext = await this.taxRegisterContextService.getTaxContext(
      taxRegisterFiscalNumber,
    );
    if (taxContext) {
      const taxOrder = this._getContextTaxOrderObject(taxContext);
      const state = await this.taxRegisterContextService.getState(
        taxRegisterFiscalNumber,
      );
      taxOrder.orderNumber = state.NextLocalNum;
      taxOrder.testing = state.Testing || false;
      return taxOrder;
    }

    const conf = await this.taxRegisterConfigService.getRegisterConfig(
      taxRegisterFiscalNumber,
    );
    const userOwnerInfo = await this.agentSigner.ctxGetUserOwnerInfo(
      conf.cashier.subjectKeyId,
    );
    if (!userOwnerInfo) {
      throw 'ПРРО не налаштовано 1.1';
    }

    const taxRegisterState = await this._getTaxRegisterState(
      taxRegisterFiscalNumber,
    );

    const orderNumber = taxRegisterState.NextLocalNum;
    const testing = taxRegisterState.Testing;

    const taxOrder = new TaxOrder({
      orderNumber: orderNumber,
      tin: conf.tin,
      ipn: conf.ipn || null,
      organizationName: conf.orgFullName,
      pointName: conf.taxRegister.objectName,
      pointAddress: conf.taxRegister.address,
      cashDeskNum: conf.taxRegister.localNumber,
      cashRegisterNum: conf.taxRegister.fiscalNumber,
      cashRegisterName: conf.taxRegister.name,
      cashier: this._getCashierName(userOwnerInfo),
      testing: testing || false,
      // shiftTotals: lastShift.Totals,
    });

    return taxOrder;
  }

  private _getContextTaxOrderObject(taxContext: TaxRegisterContext): ITaxOrder {
    const conf = taxContext.taxConfig;
    const taxObject = taxContext.registerState?.TaxObject;
    const taxRegister = this._resolveTaxRegister(conf?.taxRegister, taxObject);
    if (!taxRegister?.fiscalNumber) {
      throw new Error('ПРРО для закладу не налаштовано.');
    }
    return new TaxOrder({
      orderNumber: 0,
      tin: taxObject?.Tin || conf.tin,
      ipn: taxObject?.Ipn || conf.ipn || null,
      organizationName: taxObject?.OrgName || conf.orgFullName,
      pointName: taxObject?.Name || this._getConfigPointName(conf),
      pointAddress: taxObject?.Address || taxRegister.address,
      cashDeskNum: taxRegister.localNumber,
      cashRegisterNum: taxRegister.fiscalNumber,
      cashRegisterName: taxRegister.name,
      cashier: taxContext.registerState.Name || conf.cashier?.name,
    });
  }

  // One queue per register, keyed like the context service normalizes fiscal numbers, so a
  // number and its string share the queue. The caller gets the task's result or error.
  // A register blocked by an admin (TaxRegisterBlockService) refuses every task when it comes
  // to run, so tasks queued before the block stop too; ignoreBlock is for the admin's own ones.
  private _runRegisterExclusive<T>(
    taxRegisterFiscalNum: string | number,
    task: () => Promise<T>,
    options: { ignoreBlock?: boolean } = {},
  ): Promise<T> {
    const key = String(taxRegisterFiscalNum ?? '').trim();
    const previous = this._registerExclusiveTasks.get(key) ?? Promise.resolve();
    const current = previous
      .catch(() => undefined)
      .then(() => {
        if (!options.ignoreBlock) {
          this._blocks.assertNotBlocked(key);
        }
        return task();
      });
    this._registerExclusiveTasks.set(key, current);
    // Handles both outcomes: a finally() here would create a second promise that rejects
    // with the task's error and that nobody handles.
    const release = () => {
      if (this._registerExclusiveTasks.get(key) === current) {
        this._registerExclusiveTasks.delete(key);
      }
    };
    current.then(release, release);
    return current;
  }

  private async _makeFiscalOperation(
    taxRegisterFiscalNum: string,
    taxOrder: ITaxOrder,
  ): Promise<ITaxOrder> {
    return this._runRegisterExclusive(taxRegisterFiscalNum, () =>
      this._makeFiscalOperationExclusive(taxRegisterFiscalNum, taxOrder),
    ).catch((error) => {
      this._taxLogger.logException(error, 1, {
        operation: 'fiscalOperation',
        taxRegister: taxRegisterFiscalNum,
        documentType: taxOrder?.documentType,
        documentSubType: taxOrder?.documentSubType,
      });
      throw error;
    });
  }

  // The cashier key is validated first, before anything is sent; the document is checked when
  // its header is created (_buildXmlDoc).
  private async _makeFiscalOperationExclusive(
    taxRegisterFiscalNum: string,
    taxOrder: ITaxOrder,
  ): Promise<ITaxOrder> {
    const cnf =
      await this.taxRegisterContextService.getTaxConfig(taxRegisterFiscalNum);
    const cashier = await this._validateCashierKey(cnf);
    // FORCE_ONLINE_MODE: offline documents are ignored, not uploaded first.
    const forceOnline = this._settingsService.forceOnlineMode;
    if (!forceOnline) {
      await this._sendOfflineBeforeDocument(taxRegisterFiscalNum);
    }
    let state =
      await this.taxRegisterContextService.getState(taxRegisterFiscalNum);
    const ctxKey = cnf.cashier.subjectKeyId;
    const isOffline = (current: typeof state) =>
      current.Offline ||
      current.OfflineSession ||
      isOfflineSequenceActive(current.OfflineNextLocalNum);
    if (forceOnline && (isOffline(state) || isFakePrro(state))) {
      await this._resetContextForOnline(
        taxRegisterFiscalNum,
        ctxKey,
        'fiscalOperation',
      );
      state =
        await this.taxRegisterContextService.getState(taxRegisterFiscalNum);
      // Still offline with the server's state: the server's own session, ended first.
      if (isOffline(state)) {
        await this.endOfflineSessionOnline(taxRegisterFiscalNum);
        await this._resetContextForOnline(
          taxRegisterFiscalNum,
          ctxKey,
          'endOfflineSessionOnline',
        );
        state =
          await this.taxRegisterContextService.getState(taxRegisterFiscalNum);
      }
    }
    // The receipt the caller issues the document for (the pay dialog and _getTaxObject set it);
    // the header gets a UID of its own.
    const orderUuid = taxOrder.uid;
    const checkXml = await this._buildXmlDoc(
      taxOrder,
      taxRegisterFiscalNum,
      cashier,
    );
    if (state.Offline && !forceOnline) {
      // fakePrro: a document saved in a session numbered with made-up credentials is fake too.
      if (await this._fakePrro.isSession(state.OfflineSessionId)) {
        await this._recordFakeDocument(
          taxRegisterFiscalNum,
          checkXml,
          orderUuid,
        );
      }
      await this.taxRegisterContextService.saveDocument(
        taxRegisterFiscalNum,
        checkXml,
      );
      // Queued behind this operation on the register's exclusive chain, so the upload never
      // runs concurrently with the next document being saved.
      this.stopOfflineSessionNext(taxRegisterFiscalNum).catch((err) => {
        this._taxLogger.logException(err, 1, {
          operation: 'stopOfflineSessionNext',
          point: '_makeFiscalOperation',
          taxRegister: taxRegisterFiscalNum,
        });
      });
      return taxOrder;
    }

    // fakePrro: a fake context's numbers are made up, so its documents never go online; the
    // document starts an offline session, which the recovery upload renumbers.
    if (isFakePrro(state) && !forceOnline) {
      return this._continueOffline(
        taxRegisterFiscalNum,
        taxOrder,
        FAKE_PRRO_OFFLINE_CAUSE,
        'fakePrro',
        orderUuid,
      );
    }

    const result = await this._processTaxDocument(ctxKey, taxOrder).catch(
      (err) => {
        return {
          success: false,
          err: err,
          taxError: null,
          ticket: { ORDERTAXNUM: null },
          ticketXml: null,
        };
      },
    );

    if (result.success) {
      // Saved with its receipt as received: the service parses it, writes its fiscal number
      // into the document and, for a shift opening, puts the receipt's offline credentials in
      // force in the same write.
      let savedXml: string;
      try {
        savedXml = await this.taxRegisterContextService.saveDocument(
          taxRegisterFiscalNum,
          taxOrder.xmlDocument,
          result.ticketXml,
        );
      } catch (error) {
        // Registered by the server but not saved: like any other error in the online flow,
        // the operation continues offline. The upload revokes the original when the server
        // holds it.
        this._taxLogger.logException(error, 1, {
          operation: 'saveDocument',
          point: 'registeredOnline',
          taxRegister: taxRegisterFiscalNum,
        });
        // FORCE_ONLINE_MODE: the server holds the document, so the context takes its state.
        if (forceOnline) {
          await this._resetContextForOnline(
            taxRegisterFiscalNum,
            ctxKey,
            'saveDocument',
          ).catch((err) =>
            this._taxLogger.logException(err, 1, {
              operation: '_resetContextForOnline',
              point: 'registeredOnline',
              taxRegister: taxRegisterFiscalNum,
            }),
          );
          taxOrder.ORDERTAXNUM = result.ticket.ORDERTAXNUM;
          taxOrder.xmlDocument = this.taxRegisterService.updateTaxOrderNum(
            taxOrder.xmlDocument,
            result.ticket.ORDERTAXNUM,
          );
          return taxOrder;
        }
        return this._continueOffline(
          taxRegisterFiscalNum,
          taxOrder,
          error,
          'saveDocument',
          orderUuid,
        );
      }
      taxOrder.ORDERTAXNUM = result.ticket.ORDERTAXNUM;
      taxOrder.xmlDocument = savedXml;

      // Refresh point: after a shift closing, re-fetch the baseline and trim the journal.
      if (Number(taxOrder.documentType) === TaxDocumentType.CLOSE_SHIFT) {
        await this._synchronizeTaxRegisterContext(taxRegisterFiscalNum).catch(
          (err) => {
            this._taxLogger.logException(err, 1, {
              operation: 'synchronizeTaxRegisterContext',
              point: 'closeShift',
              taxRegister: taxRegisterFiscalNum,
            });
          },
        );
      }
      return taxOrder;
    }

    this._taxLogger.logException(<any>result, 1, {
      operation: '_processTaxDocument',
      point: 'result',
      taxRegister: taxRegisterFiscalNum,
    });

    // FORCE_ONLINE_MODE: the server's offline session blocks the document. The session is ended,
    // and the document is built again with the server's numbers and sent once more.
    if (
      forceOnline &&
      this._isOfflineSessionBlockingOnline(result.taxError) &&
      !this._offlineEndRetried.has(taxOrder)
    ) {
      this._offlineEndRetried.add(taxOrder);
      await this.endOfflineSessionOnline(taxRegisterFiscalNum);
      await this._resetContextForOnline(
        taxRegisterFiscalNum,
        ctxKey,
        'endOfflineSessionOnline',
      );
      return this._makeFiscalOperationExclusive(taxRegisterFiscalNum, taxOrder);
    }

    // tax error should be resolved by the user
    if (result.taxError) {
      const _taxError = new TaxError(result.taxError);
      // this._displayError(_taxError.text);
      throw _taxError.text;
    }

    // The document never left the terminal, so this is no reason to work offline: the upload
    // would need the same signature. Recovery and fake modes keep the sale going whatever the
    // error.
    if (
      'signerError' in result &&
      result.signerError &&
      !this._settingsService.taxFakePrro &&
      !(await this._isRecoveryRegister(taxRegisterFiscalNum))
    ) {
      throw result.signerError.message || result.signerError;
    }

    const apiError = ('err' in result && result.err) || result;
    return this._continueOffline(
      taxRegisterFiscalNum,
      taxOrder,
      apiError,
      'taxApi',
      orderUuid,
    );
  }

  // Continues an operation offline after an error in the online flow (the cause). The service
  // checks offline readiness, then saves the revoked original, OfflineBegin and the offline
  // copy in one journal write; the order takes the offline copy's numbers. Recovery mode starts
  // the session without the checks, with stand-in credentials when none are in force. When no
  // real offline session can start, fakePrro starts a fake one instead, so the sale goes on; a
  // fake context always works in fake sessions. orderUuid: the receipt the document is issued
  // for, kept with a fake document.
  private async _continueOffline(
    taxRegisterFiscalNum: string,
    taxOrder: ITaxOrder,
    cause: unknown,
    operation: string,
    orderUuid?: string,
  ): Promise<ITaxOrder> {
    await this.taxRegisterContextService.saveOfflineSessionError(
      taxRegisterFiscalNum,
      cause,
      operation,
    );

    // FORCE_ONLINE_MODE: the error goes to the user, with no offline, recovery or fake session.
    // The server may have registered the document before the error, so the next one takes the
    // server's numbers.
    if (this._settingsService.forceOnlineMode) {
      const config =
        await this.taxRegisterContextService.getTaxConfig(taxRegisterFiscalNum);
      await this._resetContextForOnline(
        taxRegisterFiscalNum,
        config.cashier.subjectKeyId,
        operation,
      ).catch((err) =>
        this._taxLogger.logException(err, 1, {
          operation: '_resetContextForOnline',
          point: operation,
          taxRegister: taxRegisterFiscalNum,
        }),
      );
      throw cause;
    }

    const fakeContext = isFakePrro(
      (await this.taxRegisterContextService.getTaxContext(taxRegisterFiscalNum))
        ?.registerState,
    );
    const fakePrro = this._settingsService.taxFakePrro || fakeContext;
    const recovery = await this._isRecoveryRegister(taxRegisterFiscalNum);
    let offlineXml: string | null = null;
    if (!fakeContext) {
      // Offline work disabled in the settings: return the original error, not an offline
      // error, and save nothing. Recovery mode goes offline regardless.
      if (
        !this.taxRegisterContextService.offlineSettings.offlineEnabled &&
        !recovery
      ) {
        if (!fakePrro) {
          throw cause;
        }
      } else {
        try {
          offlineXml = recovery
            ? await this._startUncheckedSession(
                taxRegisterFiscalNum,
                taxOrder,
                fakePrro,
                orderUuid,
              )
            : await this.taxRegisterContextService.startOfflineSession(
                taxRegisterFiscalNum,
                taxOrder.xmlDocument,
              );
        } catch (error) {
          await this.taxRegisterContextService.saveOfflineSessionError(
            taxRegisterFiscalNum,
            error,
            'startOfflineSession',
          );
          if (!fakePrro) {
            throw error;
          }
        }
      }
    }

    if (offlineXml === null) {
      offlineXml = await this._startUncheckedSession(
        taxRegisterFiscalNum,
        taxOrder,
        true,
        orderUuid,
      );
      this._taxLogger.logTrace('fakePrro: fake offline session started', {
        taxRegister: taxRegisterFiscalNum,
        fiscalNumber: this._getDocumentHead(offlineXml).ORDERTAXNUM,
      });
    }

    const head = this._getDocumentHead(offlineXml);
    taxOrder.xmlDocument = offlineXml;
    taxOrder.orderNumber = Number(head.ORDERNUM);
    taxOrder.ORDERDATE = head.ORDERDATE;
    taxOrder.ORDERTIME = head.ORDERTIME;
    taxOrder.ORDERTAXNUM = head.ORDERTAXNUM;
    taxOrder.PREVDOCHASH = undefined;
    taxOrder.offline = true;

    // Offline work disabled for the register: the service closed the session at once; its
    // upload is queued behind this operation, so the register goes online as soon as it can.
    if (await this._hasPendingOfflineUpload(taxRegisterFiscalNum)) {
      this.stopOfflineSessionNext(taxRegisterFiscalNum).catch((err) => {
        this._taxLogger.logException(err, 1, {
          operation: 'stopOfflineSessionNext',
          point: '_continueOffline',
          taxRegister: taxRegisterFiscalNum,
        });
      });
    }
    return taxOrder;
  }

  // A session closed locally whose package is not sent yet: offline, but no session open.
  private async _hasPendingOfflineUpload(
    taxRegisterFiscalNum: string,
  ): Promise<boolean> {
    const state =
      await this.taxRegisterContextService.getState(taxRegisterFiscalNum);
    return state.Offline && !state.OfflineSession;
  }

  // Before a new document: a session closed locally always goes to the server first, and, with
  // offline work disabled for the register (the context's offlineDisabled), so does an open
  // session. The register is then online, or the document is refused with the upload's error.
  // Runs on the register's exclusive chain, inside the operation. Recovery and fake registers
  // keep their own uploads.
  private async _sendOfflineBeforeDocument(
    taxRegisterFiscalNum: string,
  ): Promise<void> {
    const state =
      await this.taxRegisterContextService.getState(taxRegisterFiscalNum);
    const offlineDisabled =
      await this.taxRegisterContextService.isOfflineDisabled(
        taxRegisterFiscalNum,
      );
    const pendingUpload = state.Offline && !state.OfflineSession;
    if (
      !(
        pendingUpload ||
        (offlineDisabled && (state.Offline || state.OfflineSession))
      ) ||
      isFakePrro(state) ||
      (await this._isRecoveryRegister(taxRegisterFiscalNum))
    ) {
      return;
    }

    try {
      if (this._settingsService.taxAutoClearOfflineDocuments) {
        await this._clearOfflineExclusive(taxRegisterFiscalNum);
      } else {
        await this._stopOfflineSessionExclusive(taxRegisterFiscalNum);
      }
    } catch (error) {
      const cause =
        typeof error === 'string'
          ? error
          : (error as Error)?.message || JSON.stringify(error);
      throw new Error(
        `Офлайн документи каси не надіслано на сервер, тому новий документ не створено. ${cause}`,
      );
    }

    const after =
      await this.taxRegisterContextService.getState(taxRegisterFiscalNum);
    if (after.Offline || after.OfflineSession) {
      throw new Error(
        'Офлайн документи каси ще не надіслано на сервер, тому новий документ не створено.',
      );
    }
  }

  // FORCE_ONLINE_MODE: the context takes the server's state. Its offline documents are dropped
  // unsent and logged with their numbers. Runs in the register's exclusive section.
  private async _resetContextForOnline(
    taxRegisterFiscalNum: string,
    ctxKey: string,
    reason: string,
  ): Promise<void> {
    const documents = (
      await this.taxRegisterContextService.getDocuments(taxRegisterFiscalNum)
    ).filter(
      (document) => String(document.NumFiscal ?? '').split('.').length === 3,
    );
    const serverState = await this._taxApi.getServerState();
    const onlineState = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNum,
    );
    await this._resetContextToServer(
      taxRegisterFiscalNum,
      onlineState,
      serverState,
    );
    this._taxLogger.logTrace('Context reset to the server: force online mode', {
      taxRegister: taxRegisterFiscalNum,
      setting: 'force_online_mode',
      reason,
      count: documents.length,
      documents: documents.map((document) => ({
        numLocal: document.NumLocal,
        numFiscal: document.NumFiscal,
        type: document.CheckDocType,
        dateTime: document.DocDateTime,
      })),
    });
  }

  // The server's error for an online document while it has the register's offline session open:
  // code 9 DocumentValidationError, recognized by its message.
  private _isOfflineSessionBlockingOnline(taxError: unknown): boolean {
    const text =
      typeof taxError === 'string'
        ? taxError
        : (taxError as TaxError)?.originalError ||
          (taxError as TaxError)?.text ||
          '';
    if (!text || !text.includes(OFFLINE_SESSION_BLOCKS_ONLINE)) {
      return false;
    }

    return (
      new TaxError(text).error == TaxErrorEnum.DocumentValidationError ||
      /\b9\s+DocumentValidationError\b/.test(text)
    );
  }

  // FORCE_ONLINE_MODE: ends the offline session the tax server has open for the register, from
  // the server's data alone. The server's last document is read and hashed for PREVDOCHASH, and
  // the session end continues the server's numbering, alone in a package. Local offline documents
  // are not used. Nothing is done when the server has no offline session open.
  async endOfflineSessionOnline(
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    const fiscalNumber = String(taxRegisterFiscalNumber).trim();
    const config = (await this.taxRegisterContextService.hasContext(
      fiscalNumber,
    ))
      ? await this.taxRegisterContextService.getTaxConfig(fiscalNumber)
      : await this.taxRegisterConfigService.getRegisterConfig(fiscalNumber);
    const ctxKey = config?.cashier?.subjectKeyId;
    if (!ctxKey) {
      throw new Error(
        `ПРРО ${fiscalNumber} не налаштовано: немає ключа касира.`,
      );
    }
    const cashier = await this._validateCashierKey(config);

    const state = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      fiscalNumber,
    );
    const offlineLocalNum = Number(state?.OfflineNextLocalNum) || 1;
    if (offlineLocalNum <= 1) {
      return;
    }
    if (state.OfflineSessionId == null || state.OfflineSeed == null) {
      throw new Error(
        'Стан ПРРО не містить даних офлайн сесії (OfflineSessionId, OfflineSeed): потрібен ключ касира, що відкрив зміну, старшого касира або керівника.',
      );
    }

    // The server's last document. Its OriginalXml gives the header; it is the server's own copy
    // (no line breaks, elements moved), so its hash is only the last resort.
    const nextLocalNum = Number(state.NextLocalNum);
    const previousNumLocal = nextLocalNum - 1;
    const previous = await this._taxApi.ctxGetDocumentByLocalNum(
      ctxKey,
      fiscalNumber,
      previousNumLocal,
    );
    if (!previous) {
      throw new Error(
        `Не вдалося прочитати з ДПС останній документ каси ${fiscalNumber} (№ ${previousNumLocal}).`,
      );
    }
    const previousHead = this._getDocumentHead(previous.xml);
    console.log(
      `[prro] last shift document № ${previousNumLocal} ${
        previousHead.ORDERTAXNUM ?? ''
      }`,
    );
    console.log(previous.xml);

    // PREVDOCHASH: the server takes the hash of the document with the sender's signature (з КЕП)
    // or without it (без КЕП). Tried in turn while the server refuses the hash: the package the
    // register signed (SignedBySenderXml), the XML signed inside it, then the OriginalXml bytes.
    const hashRequired = isPreviousDocumentHashRequired(offlineLocalNum);
    const candidates: { source: string; hash: string | null }[] = [];
    if (hashRequired) {
      const signed = await this._taxApi
        .ctxGetDocumentByLocalNum(
          ctxKey,
          fiscalNumber,
          previousNumLocal,
          'SignedBySenderXml',
        )
        .catch(() => null);
      const signedContent = signed ? getCmsSignedContent(signed.bytes) : null;
      for (const candidate of [
        signed && { source: 'SignedBySenderXml', hash: sha256(signed.bytes) },
        signedContent && {
          source: 'SignedBySenderXml content',
          hash: sha256(signedContent),
        },
        { source: 'OriginalXml', hash: sha256(previous.bytes) },
      ]) {
        if (candidate && !candidates.some((c) => c.hash === candidate.hash)) {
          candidates.push(candidate);
        }
      }
      // One string per call: the app's console (MonitoringService) prints only the first argument.
      console.log(
        `[prro] PREVDOCHASH candidates ${JSON.stringify({
          candidates,
          signedBytes: signed?.bytes.length ?? null,
          signedFirstBytes: signed
            ? Array.from(signed.bytes.slice(0, 8), (byte: number) =>
                byte.toString(16).padStart(2, '0'),
              ).join(' ')
            : null,
          signedContentBytes: signedContent?.length ?? null,
        })}`,
      );
    } else {
      candidates.push({ source: 'not required', hash: null });
    }

    // The issuer of the server's last document, the cashier of the validated key; dated no
    // earlier than the last document.
    const pick = (...values: unknown[]) =>
      values
        .map((value) => String(value ?? '').trim())
        .find((value) => !!value) || '';
    const time = this._notBefore(new Date(), previousHead);

    let refused: unknown = null;
    for (const candidate of candidates) {
      const head = createDocumentHead({
        docType: TaxDocumentType.OFFLINE_END,
        uid: this._getUuid(),
        orderDate: formatOrderDate(time),
        orderTime: formatOrderTime(time),
        orderNumber: nextLocalNum,
        issuer: {
          tin: pick(previousHead.TIN, config?.tin),
          ipn: pick(previousHead.IPN, config?.ipn) || undefined,
          organizationName: pick(previousHead.ORGNM, config?.orgFullName),
          pointName: pick(
            previousHead.POINTNM,
            this._getConfigPointName(config),
          ),
          pointAddress: pick(
            previousHead.POINTADDR,
            config?.taxRegister?.address,
          ),
          cashRegisterNum: fiscalNumber,
          cashDeskNum: Number(
            pick(previousHead.CASHDESKNUM, config?.taxRegister?.localNumber),
          ),
          cashier,
        },
        testing: !!state.Testing,
        offline: {
          sessionId: String(state.OfflineSessionId),
          seed: String(state.OfflineSeed),
          localNum: offlineLocalNum,
          previousDocumentHash: candidate.hash,
        },
      });
      const endXml = TaxDocumentUtility.getChekDoc(head);
      console.log(
        `[prro] offline session end, PREVDOCHASH from ${candidate.source}: ${endXml}`,
      );
      this._taxLogger.logTrace('Offline session end online: sending', {
        taxRegister: fiscalNumber,
        numLocal: nextLocalNum,
        fiscalNumber: head.ORDERTAXNUM,
        previousNumLocal,
        previousDocumentHash: candidate.hash,
        hashSource: candidate.source,
      });

      try {
        await this._sendOfflineEndPackage(ctxKey, fiscalNumber, endXml);
      } catch (error) {
        if (!this._isPreviousHashError(error)) {
          throw error;
        }
        refused = error;
        console.log(`[prro] PREVDOCHASH from ${candidate.source} refused`);
        continue;
      }

      // "OK" alone does not show the session ended: the state is read again.
      const after = await this._taxApi.ctxGetTaxRegisterState(
        ctxKey,
        fiscalNumber,
      );
      if (isOfflineSequenceActive(after?.OfflineNextLocalNum)) {
        throw new Error(
          `Сервер ДПС прийняв пакет, але офлайн сесія каси ${fiscalNumber} лишилася відкритою.`,
        );
      }
      console.log(
        `[prro] offline session end accepted, PREVDOCHASH from ${candidate.source}`,
      );
      this._taxLogger.logTrace('Offline session end online: accepted', {
        taxRegister: fiscalNumber,
        numLocal: nextLocalNum,
        fiscalNumber: head.ORDERTAXNUM,
        hashSource: candidate.source,
      });
      return;
    }

    throw refused;
  }

  // The server's refusal of a package for the previous offline document's hash (PREVDOCHASH).
  private _isPreviousHashError(error: unknown): boolean {
    const text =
      typeof error === 'string'
        ? error
        : (error as Error)?.message || JSON.stringify(error ?? '');
    return text.includes('гешу попереднього');
  }

  // The server refuses a document dated before the previous one.
  private _notBefore(now: Date, previousHead: Record<string, string>): Date {
    if (!previousHead.ORDERDATE || !previousHead.ORDERTIME) {
      return now;
    }
    const parts = parseOrderDateTime(
      previousHead.ORDERDATE,
      previousHead.ORDERTIME,
    );
    const previous = new Date(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hours),
      Number(parts.minutes),
      Number(parts.seconds),
    );
    return Number.isNaN(previous.getTime()) || previous <= now ? now : previous;
  }

  // Header values of a document as written (leading zeros kept).
  private _getDocumentHead(xml: string): Record<string, string> {
    const document = parseXml<{
      CHECK?: { CHECKHEAD?: Record<string, string> };
      ZREP?: { ZREPHEAD?: Record<string, string> };
    }>(xml, { keepText: true });
    return document.CHECK?.CHECKHEAD || document.ZREP?.ZREPHEAD || {};
  }

  async stopOfflineSessionNext(taxRegisterFiscalNumber: string) {
    if (this._settingsService.taxAutoClearOfflineDocuments) {
      // A recovery upload under way stops after its current step.
      await this._recoveryUploads
        .get(String(taxRegisterFiscalNumber ?? '').trim())
        ?.catch(() => undefined);
      return this._runRegisterExclusive(taxRegisterFiscalNumber, () =>
        this._clearOfflineExclusive(taxRegisterFiscalNumber),
      );
    }

    if (await this._isRecoveryRegister(taxRegisterFiscalNumber)) {
      return this._runRecoveryUpload(taxRegisterFiscalNumber);
    }

    return this._runRegisterExclusive(taxRegisterFiscalNumber, () =>
      this._stopOfflineSessionExclusive(taxRegisterFiscalNumber),
    );
  }

  private async _stopOfflineSessionExclusive(
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    try {
      await this._stopOfflineSessionExclusiveInner(taxRegisterFiscalNumber);
    } catch (error) {
      await this.taxRegisterContextService.saveOfflineSessionError(
        taxRegisterFiscalNumber,
        error,
        'stopOfflineSession',
      );
      throw error;
    }
  }

  private async _clearOfflineExclusive(
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    try {
      await this._clearOfflineExclusiveInner(taxRegisterFiscalNumber);
    } catch (error) {
      await this.taxRegisterContextService.saveOfflineSessionError(
        taxRegisterFiscalNumber,
        error,
        'autoClearOfflineDocuments',
      );
      throw error;
    }
  }

  // tax_auto_clear_offline_documents: the admin turns it on for a device whose offline sequence
  // is broken. Instead of the upload, the register's offline documents are deleted and the
  // context takes the server's state, so the register works online again; a fake context is
  // replaced by the real one, and the register's recovery queue is cleared. An offline session the server has open (part of the session
  // reached it) is ended first with a session end at the server's position, as the online work
  // cannot go on before it. Nothing changes while the server cannot be reached.
  private async _clearOfflineExclusiveInner(
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    const taxContext = await this.taxRegisterContextService.getTaxContext(
      taxRegisterFiscalNumber,
    );
    if (!taxContext) {
      return;
    }
    const currentState = await this.taxRegisterContextService.getState(
      taxRegisterFiscalNumber,
    );
    const fake = isFakePrro(taxContext.registerState);
    if (
      !currentState.Offline &&
      !isOfflineSequenceActive(currentState.OfflineNextLocalNum) &&
      !fake
    ) {
      return;
    }

    const config = taxContext.taxConfig;
    const ctxKey = config.cashier.subjectKeyId;
    const serverState = await this._taxApi.getServerState().catch((err) => {
      throw `Сервер податкової недоступний. ${JSON.stringify(err)}`;
    });
    let onlineState = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNumber,
    );

    const serverSessionOpen = isOfflineSequenceActive(
      onlineState.OfflineNextLocalNum,
    );
    if (serverSessionOpen) {
      await this._endServerOfflineSession(ctxKey, taxRegisterFiscalNumber);
      onlineState = await this._taxApi.ctxGetTaxRegisterState(
        ctxKey,
        taxRegisterFiscalNumber,
      );
      if (isOfflineSequenceActive(onlineState.OfflineNextLocalNum)) {
        throw new Error(
          'Сервер не завершив офлайн сесію каси. Офлайн документи не видалено.',
        );
      }
    }

    const documentsBefore = (
      await this.taxRegisterContextService.getDocuments(taxRegisterFiscalNumber)
    ).length;
    await this._resetContextToServer(
      taxRegisterFiscalNumber,
      onlineState,
      serverState,
    );
    // The recovery queue goes too: the real documents waiting for the backend, and the session
    // id if it was made up.
    const deliveries = await this._fakePrro.clearRegister(
      taxRegisterFiscalNumber,
      [currentState.OfflineSessionId],
    );

    this._taxLogger.logTrace(
      'Offline documents cleared automatically, the register is online',
      {
        taxRegister: taxRegisterFiscalNumber,
        documents: documentsBefore,
        deliveries,
        fakePrro: fake,
        serverSessionEnded: serverSessionOpen,
        nextLocalNum: onlineState.NextLocalNum,
      },
    );
  }

  // Deletes the register's offline documents and puts the server's state in force, so the register
  // works online; a fake context becomes the real one. A journal that keeps an online document the
  // server does not have starts over from the server, with the register's on/off setting.
  private async _resetContextToServer(
    taxRegisterFiscalNumber: string,
    onlineState: TransactionsRegistrarStateResponse,
    serverState: TaxServerState,
  ): Promise<void> {
    const taxContext = await this.taxRegisterContextService.getTaxContext(
      taxRegisterFiscalNumber,
    );
    const config = taxContext.taxConfig;
    const lastShiftTotals = await this._getContextLastShiftTotals(
      config.cashier.subjectKeyId,
      taxRegisterFiscalNumber,
    );
    await this.taxRegisterContextService.clearOfflineDocuments(
      taxRegisterFiscalNumber,
    );
    let initialized = await this.taxRegisterContextService.initContext(
      config,
      onlineState,
      lastShiftTotals,
      serverState,
      true,
    );
    if (!initialized) {
      await this.taxRegisterContextService.removeContext(
        taxRegisterFiscalNumber,
      );
      initialized = await this.taxRegisterContextService.initContext(
        config,
        onlineState,
        lastShiftTotals,
        serverState,
      );
      if (!initialized) {
        throw new Error('Не вдалося оновити контекст каси даними сервера.');
      }
      await this.taxRegisterContextService.setDisabled(
        taxRegisterFiscalNumber,
        taxContext.disabled,
      );
    }
  }

  // An offline session without a check or a Z-report: the server refuses to end it ("Пакет
  // порожній"). When the server has none of it, nothing in it needs the server (a shift opening
  // alone, an OfflineBegin whose check the server registered online): its documents are dropped
  // and the register goes online with the server's state; an open shift is opened again with the
  // next operation. When the server has begun it, only a check can end it: the session stays open
  // and the upload after the next check ends it.
  private async _dropEmptyOfflineSession(
    taxRegisterFiscalNumber: string,
    onlineState: TransactionsRegistrarStateResponse,
  ): Promise<void> {
    if (isOfflineSequenceActive(onlineState.OfflineNextLocalNum)) {
      throw new Error(EMPTY_SERVER_SESSION_ERROR);
    }
    const serverState = await this._taxApi.getServerState();
    const documents = await this.taxRegisterContextService.getDocuments(
      taxRegisterFiscalNumber,
    );
    await this._resetContextToServer(
      taxRegisterFiscalNumber,
      onlineState,
      serverState,
    );
    this._taxLogger.logTrace(
      'Offline session without checks dropped, the register is online',
      {
        taxRegister: taxRegisterFiscalNumber,
        documents: documents.length,
        nextLocalNum: onlineState.NextLocalNum,
      },
    );
  }

  // Ends the offline session the server has open: the session end, with the register's header,
  // at the server's position and dated no earlier than its last document.
  private async _endServerOfflineSession(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    const server = await this._readRecoveryServer(
      ctxKey,
      taxRegisterFiscalNumber,
    );
    const endXml = renumberAtPosition(
      clampDocumentTime(
        replaceDocumentIssuer(
          await this._stopOfflineSessionXml(taxRegisterFiscalNumber),
          await this._getRecoveryIssuer(taxRegisterFiscalNumber, server.state),
        ),
        server.position.previousDocumentTime,
      ),
      server.position,
    );
    const sent = await this._sendRecoveryPackage(
      ctxKey,
      taxRegisterFiscalNumber,
      endXml,
    );
    if (sent.rejected) {
      throw sent.rejected;
    }
    this._taxLogger.logTrace(
      'Offline session ended on the server before clearing',
      {
        taxRegister: taxRegisterFiscalNumber,
        fiscalNumber: this._getDocumentHead(endXml).ORDERTAXNUM,
      },
    );
  }

  private async _stopOfflineSessionExclusiveInner(
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    await this._taxApi.getServerState().catch((err) => {
      throw `Сервер податкової недоступний. ${JSON.stringify(err)}`;
    });

    const currentState = await this.taxRegisterContextService.getState(
      taxRegisterFiscalNumber,
    );
    const conf = await this.taxRegisterContextService.getTaxConfig(
      taxRegisterFiscalNumber,
    );
    // The key signs the documents and the session end: validated before anything is sent.
    await this._validateCashierKey(conf);

    const ctxKey = conf.cashier.subjectKeyId;
    // Recovery state is requested with the credentials of the session being uploaded, so that
    // OfflineNextLocalNum is that session's upload position.
    const onlineState = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNumber,
      this._toOptionalString(currentState.OfflineSessionId),
      this._toOptionalString(currentState.OfflineSeed),
    );

    if (
      currentState.OfflineSession &&
      (await this._completeLostSessionEnd(
        ctxKey,
        taxRegisterFiscalNumber,
        onlineState.NextLocalNum,
      ))
    ) {
      return;
    }

    const isOnline =
      currentState.NextLocalNum == onlineState.NextLocalNum &&
      !isOfflineSequenceActive(onlineState.OfflineNextLocalNum);

    if (isOnline) {
      // A session closed locally that the server already ended (its answer was lost): the end
      // is completed with the credentials the server holds now, then the register syncs.
      if (currentState.Offline && !currentState.OfflineSession) {
        await this._completeServerSessionEnd(
          ctxKey,
          taxRegisterFiscalNumber,
          await this._stopOfflineSessionXml(taxRegisterFiscalNumber),
        );
      }
      await this._synchronizeTaxRegisterContext(taxRegisterFiscalNumber);
      return;
    }

    if (!currentState.Offline) {
      return;
    }

    const serverOfflineNextLocalNum = Number(onlineState.OfflineNextLocalNum);
    if (
      !Number.isInteger(serverOfflineNextLocalNum) ||
      serverOfflineNextLocalNum < 1
    ) {
      throw new Error(
        `Сервер повернув некоректний номер наступного офлайн документа: ${onlineState.OfflineNextLocalNum}`,
      );
    }

    // The whole session, the documents the server already has included; a journal without its
    // start is left to the checks below.
    const sessionDocuments = await this.taxRegisterContextService
      .getOfflineDocuments(taxRegisterFiscalNumber, 1)
      .catch(() => null);
    if (sessionDocuments && !sessionDocuments.some(isFinancialDocument)) {
      await this._dropEmptyOfflineSession(taxRegisterFiscalNumber, onlineState);
      return;
    }

    await this.taxRegisterContextService.revokeLastOnline(
      taxRegisterFiscalNumber,
      onlineState.NextLocalNum,
      onlineState.OfflineNextLocalNum,
    );

    // Ordinary packages: an accepted package changes nothing locally. An empty list means the
    // server already has every ordinary document; only the end remains.
    const offlineDocuments =
      await this.taxRegisterContextService.getOfflineDocuments(
        taxRegisterFiscalNumber,
        serverOfflineNextLocalNum,
      );
    await this._sendOfflineDocuments(
      ctxKey,
      taxRegisterFiscalNumber,
      offlineDocuments,
    );

    // The end-session document goes alone in the final package and is saved only after the
    // server answers with the next session's credentials.
    const stopOfflineShiftDoc = await this._stopOfflineSessionXml(
      taxRegisterFiscalNumber,
    );
    let nextSession: OfflineSessionState;
    try {
      const endResponse = await this._sendOfflineEndPackage(
        ctxKey,
        taxRegisterFiscalNumber,
        stopOfflineShiftDoc,
      );
      if (endResponse.accepted) {
        throw new Error(
          'Сервер відповів "OK" на пакет із завершенням офлайн сесії. Результат буде перевірено під час наступної спроби.',
        );
      }

      nextSession = endResponse.state;
      if (!nextSession) {
        throw new Error(
          'Відповідь на завершення офлайн сесії не містить OfflineSessionId і OfflineSeed. Результат буде перевірено під час наступної спроби.',
        );
      }
    } catch (error) {
      // The server may hold the end. Asked at once, as the next document would go into this
      // session. Without the session's credentials: naming it could cancel its closure.
      const completed = await this._taxApi
        .ctxGetTaxRegisterState(ctxKey, taxRegisterFiscalNumber)
        .then((state) =>
          this._completeLostSessionEnd(
            ctxKey,
            taxRegisterFiscalNumber,
            state.NextLocalNum,
          ),
        )
        .catch((err) => {
          this._taxLogger.logException(err, 1, {
            operation: 'completeLostSessionEnd',
            point: 'stopOfflineSession',
            taxRegister: taxRegisterFiscalNumber,
          });
          return false;
        });
      if (completed) {
        return;
      }
      throw error;
    }

    await this.taxRegisterContextService.completeOfflineSession(
      taxRegisterFiscalNumber,
      stopOfflineShiftDoc,
      nextSession,
    );

    await this._synchronizeTaxRegisterContext(taxRegisterFiscalNumber).catch(
      (err) => {
        this._taxLogger.logException(err, 1, {
          operation: 'synchronizeTaxRegisterContext',
          point: 'stopOfflineSession',
          taxRegister: taxRegisterFiscalNumber,
        });
      },
    );
  }

  // An open session whose end the server accepted though the answer was lost, so the journal has
  // no end: the server's last document is an end naming a document of this session in PREVDOCHASH.
  // The numbers alone can not show it, as documents may have been saved in the session since. The
  // end is saved as the server has it, then the register syncs; documents saved after it can not
  // be sent, which is reported. Returns whether the session was completed.
  private async _completeLostSessionEnd(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    serverNextLocalNum: number,
  ): Promise<boolean> {
    const documents = await this.taxRegisterContextService.getDocuments(
      taxRegisterFiscalNumber,
    );
    const begin = [...documents]
      .reverse()
      .find((document) => document.CheckDocType === 'OfflineBegin');
    // OfflineBegin, one document and the end: the server can hold no end before that.
    if (!begin || Number(serverNextLocalNum) < begin.NumLocal + 3) {
      return false;
    }

    const sequence = selectRecoveryDocuments(documents, [
      await this._getServerDocumentXml(
        ctxKey,
        taxRegisterFiscalNumber,
        Number(serverNextLocalNum) - 1,
      ),
    ]);
    if (!sequence.serverSessionEnd) {
      return false;
    }
    if (sequence.send.length) {
      throw new Error(SERVER_SESSION_ENDED_ERROR);
    }

    await this._completeServerSessionEnd(
      ctxKey,
      taxRegisterFiscalNumber,
      sequence.serverSessionEnd,
    );
    this._taxLogger.logTrace('Offline session end found on the server', {
      taxRegister: taxRegisterFiscalNumber,
      fiscalNumber: this._getDocumentHead(sequence.serverSessionEnd)
        .ORDERTAXNUM,
    });
    await this._synchronizeTaxRegisterContext(taxRegisterFiscalNumber).catch(
      (err) => {
        this._taxLogger.logException(err, 1, {
          operation: 'synchronizeTaxRegisterContext',
          point: 'completeLostSessionEnd',
          taxRegister: taxRegisterFiscalNumber,
        });
      },
    );
    return true;
  }

  // Saves an end the server accepted though its answer was lost, with the credentials the server
  // holds now.
  private async _completeServerSessionEnd(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    endXml: string,
  ): Promise<void> {
    const nextSession = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNumber,
    );
    if (
      nextSession.OfflineSessionId == null ||
      nextSession.OfflineSeed == null
    ) {
      throw new Error(
        'Сервер уже має завершення офлайн сесії, але не повернув даних наступної сесії. Результат буде перевірено під час наступної спроби.',
      );
    }
    await this.taxRegisterContextService.completeOfflineSession(
      taxRegisterFiscalNumber,
      endXml,
      {
        OfflineSessionId: String(nextSession.OfflineSessionId),
        OfflineSeed: String(nextSession.OfflineSeed),
      },
    );
  }

  // Full synchronization: replace the stored state and totals and clear the journal, but only
  // when the server has every local document and no offline session is open locally.
  private async _synchronizeTaxRegisterContext(
    taxRegisterFiscalNumber: string,
  ): Promise<boolean> {
    const currentState = await this.taxRegisterContextService.getState(
      taxRegisterFiscalNumber,
    );
    if (currentState.Offline || currentState.OfflineSession) {
      return false;
    }

    const conf = await this.taxRegisterContextService.getTaxConfig(
      taxRegisterFiscalNumber,
    );
    const ctxKey = conf.cashier.subjectKeyId;
    const serverState = await this._taxApi.getServerState();
    const onlineState = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNumber,
    );
    const lastShiftTotals = await this._getContextLastShiftTotals(
      ctxKey,
      taxRegisterFiscalNumber,
    );

    if (
      Number(onlineState?.NextLocalNum) !== Number(currentState.NextLocalNum)
    ) {
      return false;
    }
    if (isOfflineSequenceActive(onlineState.OfflineNextLocalNum)) {
      return false;
    }
    // A response without offline credentials (not signed by the shift opener, a senior user,
    // or a manager) must not erase credentials the context already holds.
    const hasLocalCredentials =
      currentState.OfflineSessionId != null && currentState.OfflineSeed != null;
    const hasServerCredentials =
      onlineState.OfflineSessionId != null && onlineState.OfflineSeed != null;
    if (hasLocalCredentials && !hasServerCredentials) {
      return false;
    }

    // Still in the register's exclusive section: no document can be saved meanwhile.
    return this.taxRegisterContextService.initContext(
      conf,
      onlineState,
      lastShiftTotals,
      serverState,
      true,
    );
  }

  // Sends ordinary offline documents with SendOflPackage, one package at a time. Each package
  // must be acknowledged with "OK" before the next one is sent.
  private async _sendOfflineDocuments(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    xmlDocuments: string[],
  ): Promise<void> {
    const signedDocuments: string[] = [];
    for (const xmlDocument of xmlDocuments) {
      signedDocuments.push(
        await this.agentSigner.ctxSignData(ctxKey, xmlDocument),
      );
    }

    let start = 0;
    while (start < signedDocuments.length) {
      const offlinePackage = await prepareOfflinePackage(
        (data) => this.agentSigner.ctxSignData(ctxKey, data),
        () => this._getUuid(),
        taxRegisterFiscalNumber,
        signedDocuments,
        xmlDocuments,
        start,
        this.taxRegisterContextService.offlineSettings
          .offlinePackageDocumentLimit,
      );
      const response = readOfflinePackageResponse(
        await this._taxApi.sendOfflinePackageCommand(
          offlinePackage.signedCommand,
        ),
        offlinePackage.uid,
      );
      if (!response.accepted) {
        throw new Error(
          response.state
            ? 'Сервер повернув дані нової офлайн сесії на пакет без завершення сесії.'
            : 'Невідома відповідь сервера на офлайн пакет.',
        );
      }

      start += offlinePackage.count;
    }
  }

  // Sends the end-session document alone in the final package.
  private async _sendOfflineEndPackage(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    endSessionXml: string,
  ): Promise<OfflinePackageResponse> {
    const signedEnd = await this.agentSigner.ctxSignData(ctxKey, endSessionXml);
    const offlinePackage = await prepareOfflinePackage(
      (data) => this.agentSigner.ctxSignData(ctxKey, data),
      () => this._getUuid(),
      taxRegisterFiscalNumber,
      [signedEnd],
      [endSessionXml],
      0,
    );
    return readOfflinePackageResponse(
      await this._taxApi.sendOfflinePackageCommand(
        offlinePackage.signedCommand,
      ),
      offlinePackage.uid,
    );
  }

  private _toOptionalString(value: string | number | null | undefined): string {
    return value === null || value === undefined || value === ''
      ? null
      : String(value);
  }

  // Recovery mode upload (the tax_recovery_mode setting; see tax-recovery.ts). Every offline
  // document goes alone in a package. A document the server rejects is renumbered, with every
  // document after it, from the server's state and its last document, then sent again; a second
  // rejection stops the upload. Revoked documents and duplicates are not sent. Each document is
  // a step of its own on the register's queue, so sales go on between them; one upload runs per
  // register at a time.
  private _runRecoveryUpload(taxRegisterFiscalNumber: string): Promise<void> {
    const key = String(taxRegisterFiscalNumber ?? '').trim();
    const running = this._recoveryUploads.get(key);
    if (running) {
      return running;
    }

    const upload: RecoveryUpload = { server: null, documents: [] };
    const run = (async () => {
      try {
        let finished = false;
        while (!finished) {
          finished = await this._runRegisterExclusive(
            taxRegisterFiscalNumber,
            () => this._recoveryUploadStep(taxRegisterFiscalNumber, upload),
          );
        }
      } catch (error) {
        await this.taxRegisterContextService.saveOfflineSessionError(
          taxRegisterFiscalNumber,
          error,
          'recoveryUpload',
        );
        throw error;
      } finally {
        this._recoveryUploads.delete(key);
      }
    })();
    this._recoveryUploads.set(key, run);
    return run;
  }

  // One step of a recovery upload: sends the next document, or ends the session when the server
  // has them all. Returns true when the upload is over.
  private async _recoveryUploadStep(
    taxRegisterFiscalNumber: string,
    upload: RecoveryUpload,
  ): Promise<boolean> {
    // tax_auto_clear_offline_documents turned on meanwhile: the documents are cleared instead.
    if (this._settingsService.taxAutoClearOfflineDocuments) {
      return true;
    }
    const config = await this.taxRegisterContextService.getTaxConfig(
      taxRegisterFiscalNumber,
    );
    const ctxKey = config.cashier.subjectKeyId;

    if (!upload.server) {
      const state = await this.taxRegisterContextService.getState(
        taxRegisterFiscalNumber,
      );
      if (!state.Offline) {
        await this._synchronizeTaxRegisterContext(
          taxRegisterFiscalNumber,
        ).catch((error) =>
          this._logRecoverySyncError(taxRegisterFiscalNumber, error),
        );
        return true;
      }
      upload.server = await this._readRecoveryServer(
        ctxKey,
        taxRegisterFiscalNumber,
      );
    }

    if (!upload.documents.length) {
      const sequence = selectRecoveryDocuments(
        await this.taxRegisterContextService.getDocuments(
          taxRegisterFiscalNumber,
        ),
        upload.server.documents,
      );
      if (sequence.duplicates.length) {
        await this.taxRegisterContextService.unsafeReplaceDocuments(
          taxRegisterFiscalNumber,
          sequence.duplicates.map((document) => ({
            xml: document.xmlCheck,
            replacement: null,
          })),
        );
      }
      await this._logRecoverySkips(taxRegisterFiscalNumber, sequence);
      // Documents the server already has (their answer lost): their real versions go to the
      // backend as well.
      for (const document of sequence.onServer) {
        await this._deliverRealDocument(
          taxRegisterFiscalNumber,
          document.xmlCheck,
        );
      }

      if (sequence.serverSessionEnd) {
        if (sequence.send.length) {
          throw new Error(SERVER_SESSION_ENDED_ERROR);
        }
        // The server accepted the session end but its response was lost: save the server's end.
        await this.taxRegisterContextService.completeOfflineSession(
          taxRegisterFiscalNumber,
          sequence.serverSessionEnd,
          {
            OfflineSessionId: String(upload.server.state.OfflineSessionId),
            OfflineSeed: String(upload.server.state.OfflineSeed),
          },
        );
        await this._synchronizeTaxRegisterContext(
          taxRegisterFiscalNumber,
        ).catch((error) =>
          this._logRecoverySyncError(taxRegisterFiscalNumber, error),
        );
        return true;
      }

      // A session without a check or Z-report cannot be ended: neither among the documents the
      // server has (while its session is open, its last document belongs to it) nor among those
      // left to send — duplicates of checks the server registered online are not.
      const serverLast =
        upload.server.documents[upload.server.documents.length - 1];
      if (
        ![...sequence.onServer, ...sequence.send].some((document) =>
          isFinancialDocument(document.xmlCheck),
        ) &&
        !(
          upload.server.position.offlineNextLocalNum > 1 &&
          isFinancialDocument(serverLast)
        )
      ) {
        await this._dropEmptyOfflineSession(
          taxRegisterFiscalNumber,
          upload.server.state,
        );
        return true;
      }

      if (!sequence.send.length) {
        await this._finishRecoverySession(
          ctxKey,
          taxRegisterFiscalNumber,
          upload,
        );
        return true;
      }
      const issuer = await this._getRecoveryIssuer(
        taxRegisterFiscalNumber,
        upload.server.state,
      );
      const converted = needsConversion(sequence, upload.server.position);
      if (
        converted ||
        needsIssuerUpdate(sequence.send, issuer) ||
        needsTimeUpdate(
          sequence.send,
          upload.server.position.previousDocumentTime,
        )
      ) {
        // A fake session never reaches the server as saved, nor a header other than the
        // register's, nor a document dated before the server's last one: the documents get the
        // register's header, a date no earlier than the document before them and the server's
        // numbering first, then go one per package. A header and a date that are right stay,
        // and so do the numbers of documents that already continue the server's.
        const rebuild = planRecoveryRebuild(
          { ...sequence, duplicates: [] },
          upload.server.position,
          issuer,
        );
        await this.taxRegisterContextService.unsafeReplaceDocuments(
          taxRegisterFiscalNumber,
          rebuild.changes,
        );
        this._taxLogger.logTrace(
          'Recovery: documents rebuilt with the register header and the server numbering',
          {
            taxRegister: taxRegisterFiscalNumber,
            documents: rebuild.documents.length,
            converted,
            retimed: rebuild.retimed,
            serverLastDocumentTime: upload.server.position.previousDocumentTime,
          },
        );
        upload.documents = rebuild.documents;
        if (!upload.documents.length) {
          return false;
        }
      } else {
        upload.documents = sequence.send.map((document) => document.xmlCheck);
      }
    }

    let xml = upload.documents[0];
    let sent = await this._sendRecoveryPackage(
      ctxKey,
      taxRegisterFiscalNumber,
      xml,
    );
    if (sent.rejected) {
      this._taxLogger.logTrace(
        'Recovery: document rejected, rebuilding from the server',
        {
          taxRegister: taxRegisterFiscalNumber,
          fiscalNumber: this._getDocumentHead(xml).ORDERTAXNUM,
          error: String((sent.rejected as Error)?.message || sent.rejected),
        },
      );
      // Renumbered, with every document after it, from where the server actually is.
      upload.server = await this._readRecoveryServer(
        ctxKey,
        taxRegisterFiscalNumber,
      );
      const sequence = selectRecoveryDocuments(
        await this.taxRegisterContextService.getDocuments(
          taxRegisterFiscalNumber,
        ),
        upload.server.documents,
      );
      const rebuild = planRecoveryRebuild(
        sequence,
        upload.server.position,
        await this._getRecoveryIssuer(
          taxRegisterFiscalNumber,
          upload.server.state,
        ),
      );
      await this.taxRegisterContextService.unsafeReplaceDocuments(
        taxRegisterFiscalNumber,
        rebuild.changes,
      );
      await this._logRecoverySkips(taxRegisterFiscalNumber, sequence);
      if (rebuild.retimed) {
        this._taxLogger.logTrace(
          "Recovery: documents dated before the server's last document take its time",
          {
            taxRegister: taxRegisterFiscalNumber,
            retimed: rebuild.retimed,
            serverLastDocumentTime: upload.server.position.previousDocumentTime,
          },
        );
      }
      upload.documents = rebuild.documents;
      if (!upload.documents.length) {
        return false;
      }
      xml = upload.documents[0];
      sent = await this._sendRecoveryPackage(
        ctxKey,
        taxRegisterFiscalNumber,
        xml,
      );
      if (sent.rejected) {
        throw sent.rejected;
      }
    }

    this._taxLogger.logTrace('Recovery: document accepted', {
      taxRegister: taxRegisterFiscalNumber,
      numLocal: this._getDocumentHead(xml).ORDERNUM,
      fiscalNumber: this._getDocumentHead(xml).ORDERTAXNUM,
    });
    await this._deliverRealDocument(taxRegisterFiscalNumber, xml);
    upload.documents.shift();
    // The document before the server's session stays; the one just sent is now its last.
    upload.server.documents = [upload.server.documents[0], xml];
    return false;
  }

  // Ends the session once the server has every document. The end goes alone in a package and is
  // rebuilt from the server's position if rejected; it is saved with the next session's
  // credentials from the response, then the context is synchronized.
  private async _finishRecoverySession(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    upload: RecoveryUpload,
  ): Promise<void> {
    let endXml = await this._getRecoverySessionEndXml(
      taxRegisterFiscalNumber,
      upload.server?.state,
      readDocumentTime(upload.server?.documents.slice(-1)[0]),
    );
    let sent = await this._sendRecoveryPackage(
      ctxKey,
      taxRegisterFiscalNumber,
      endXml,
    );
    if (sent.rejected) {
      upload.server = await this._readRecoveryServer(
        ctxKey,
        taxRegisterFiscalNumber,
      );
      endXml = renumberAtPosition(
        clampDocumentTime(
          replaceDocumentIssuer(
            endXml,
            await this._getRecoveryIssuer(
              taxRegisterFiscalNumber,
              upload.server.state,
            ),
          ),
          upload.server.position.previousDocumentTime,
        ),
        upload.server.position,
      );
      sent = await this._sendRecoveryPackage(
        ctxKey,
        taxRegisterFiscalNumber,
        endXml,
      );
      if (sent.rejected) {
        throw sent.rejected;
      }
    }

    if (sent.response.accepted || !sent.response.state) {
      throw new Error(
        'Відповідь на завершення офлайн сесії не містить OfflineSessionId і OfflineSeed. Результат буде перевірено під час наступної спроби.',
      );
    }
    await this.taxRegisterContextService.completeOfflineSession(
      taxRegisterFiscalNumber,
      endXml,
      sent.response.state,
    );
    await this._synchronizeTaxRegisterContext(taxRegisterFiscalNumber).catch(
      (error) => this._logRecoverySyncError(taxRegisterFiscalNumber, error),
    );
  }

  // The session end with the register's header, like every document the upload sends: the
  // service gives it the issuer of the session's first document, which may have reached the
  // server under an older registration. It is dated no earlier than the server's last document
  // (lastDocumentTime): the upload may have dated the documents before it later than the
  // terminal's clock. A changed header or date gets its fiscal number recomputed.
  private async _getRecoverySessionEndXml(
    taxRegisterFiscalNumber: string,
    serverState: TransactionsRegistrarStateResponse | null | undefined,
    lastDocumentTime: DocumentTime | null,
  ): Promise<string> {
    const endXml = await this._stopOfflineSessionXml(taxRegisterFiscalNumber);
    const prepared = clampDocumentTime(
      replaceDocumentIssuer(
        endXml,
        await this._getRecoveryIssuer(taxRegisterFiscalNumber, serverState),
      ),
      lastDocumentTime,
    );
    if (prepared === endXml) {
      return endXml;
    }
    const state = await this.taxRegisterContextService.getState(
      taxRegisterFiscalNumber,
    );
    return recomputeOfflineNumber(prepared, state.OfflineSeed);
  }

  // The server's position for the next offline document: its state and its documents — the last
  // one (for PREVDOCHASH, and to find where the upload resumes) and, while its session is open,
  // the one before that session (to recognise a sale it registered online). The state carries
  // the session credentials only when signed by the shift opener, a senior user or a manager.
  private async _readRecoveryServer(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
  ): Promise<RecoveryServer> {
    const state = await this._taxApi.ctxGetTaxRegisterState(
      ctxKey,
      taxRegisterFiscalNumber,
    );
    const nextLocalNum = Number(state?.NextLocalNum);
    const offlineNextLocalNum = Number(state?.OfflineNextLocalNum) || 1;
    if (!Number.isInteger(nextLocalNum) || nextLocalNum < 1) {
      throw new Error(
        `Сервер повернув некоректний номер наступного документа: ${state?.NextLocalNum}`,
      );
    }
    if (
      this._toOptionalString(state.OfflineSessionId) === null ||
      this._toOptionalString(state.OfflineSeed) === null
    ) {
      throw new Error(
        'Стан ПРРО не містить даних офлайн сесії (OfflineSessionId, OfflineSeed): потрібен ключ касира, що відкрив зміну, старшого касира або керівника.',
      );
    }

    const last =
      nextLocalNum > 1
        ? await this._getServerDocumentXml(
            ctxKey,
            taxRegisterFiscalNumber,
            nextLocalNum - 1,
          )
        : null;
    const beforeSessionNum = nextLocalNum - offlineNextLocalNum;
    const beforeSession =
      offlineNextLocalNum <= 1
        ? last
        : beforeSessionNum >= 1
        ? await this._getServerDocumentXml(
            ctxKey,
            taxRegisterFiscalNumber,
            beforeSessionNum,
          )
        : null;

    return {
      state,
      position: {
        nextLocalNum,
        offlineNextLocalNum,
        offlineSessionId: String(state.OfflineSessionId),
        offlineSeed: String(state.OfflineSeed),
        previousDocumentHash: last ? hashTaxDocument(last) : null,
        previousDocumentTime: readDocumentTime(last),
        shiftOpened: Number(state.ShiftState) === 1,
      },
      documents: [beforeSession, last].filter(
        (xml, index, all) => !!xml && all.indexOf(xml) === index,
      ),
    };
  }

  // The original XML of a server document by its local number: a check, or a Z-report.
  private async _getServerDocumentXml(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    numLocal: number,
  ): Promise<string | null> {
    return (
      (await this._taxApi.ctxGetCheckXmlByLocalNum(
        ctxKey,
        taxRegisterFiscalNumber,
        numLocal,
      )) ??
      (await this._taxApi.ctxGetZRepXmlByLocalNum(
        ctxKey,
        taxRegisterFiscalNumber,
        numLocal,
      ))
    );
  }

  // Sends one document alone in a package. Returns the response, or the server's rejection of
  // the document; a request that did not reach the server throws.
  private async _sendRecoveryPackage(
    ctxKey: string,
    taxRegisterFiscalNumber: string,
    xml: string,
  ): Promise<{ response?: OfflinePackageResponse; rejected?: unknown }> {
    const signedDocument = await this.agentSigner.ctxSignData(ctxKey, xml);
    const offlinePackage = await prepareOfflinePackage(
      (data) => this.agentSigner.ctxSignData(ctxKey, data),
      () => this._getUuid(),
      taxRegisterFiscalNumber,
      [signedDocument],
      [xml],
      0,
    );
    const result = await this._taxApi.sendOfflinePackageCommand(
      offlinePackage.signedCommand,
    );
    if (
      !result?.success &&
      !isDocumentRejectedByServer(result?.httpStatusCode)
    ) {
      throw result?.error || new Error('Сервер податкової недоступний.');
    }

    try {
      return {
        response: readOfflinePackageResponse(result, offlinePackage.uid),
      };
    } catch (error) {
      return { rejected: error };
    }
  }

  private _logRecoverySyncError(
    taxRegisterFiscalNumber: string,
    error: unknown,
  ): void {
    this._taxLogger.logException(error, 1, {
      operation: 'synchronizeTaxRegisterContext',
      point: 'recoveryUpload',
      taxRegister: taxRegisterFiscalNumber,
    });
  }

  // Credentials for an offline session started without the checks: none (the service takes
  // those in force) when there are some, otherwise made-up ones — in the server's format for a
  // fake session, recovery mode's stand-ins otherwise.
  private async _getUnsafeSessionCredentials(
    taxRegisterFiscalNumber: string,
    fake: boolean,
  ): Promise<OfflineSessionState | undefined> {
    const state = await this.taxRegisterContextService.getState(
      taxRegisterFiscalNumber,
    );
    if (
      this._toOptionalString(state.OfflineSessionId) !== null &&
      this._toOptionalString(state.OfflineSeed) !== null
    ) {
      return undefined;
    }
    return fake ? createFakePrroCredentials() : RECOVERY_OFFLINE_SESSION;
  }

  // An offline session started without the checks. With made-up credentials its documents are
  // fake: the session id and the document are remembered before the journal write (which also
  // stages them for the backend outbox), and the offline copy's made-up number afterwards.
  private async _startUncheckedSession(
    taxRegisterFiscalNumber: string,
    taxOrder: ITaxOrder,
    fake: boolean,
    orderUuid?: string,
  ): Promise<string> {
    const credentials = await this._getUnsafeSessionCredentials(
      taxRegisterFiscalNumber,
      fake,
    );
    if (credentials) {
      await this._fakePrro.addSession(String(credentials.OfflineSessionId));
      await this._recordFakeDocument(
        taxRegisterFiscalNumber,
        taxOrder.xmlDocument,
        orderUuid,
      );
    }
    const offlineXml =
      await this.taxRegisterContextService.unsafeStartOfflineSession(
        taxRegisterFiscalNumber,
        taxOrder.xmlDocument,
        credentials,
      );
    if (credentials) {
      await this._recordFakeDocument(
        taxRegisterFiscalNumber,
        offlineXml,
        orderUuid,
      );
    }
    return offlineXml;
  }

  // fakePrro: remembers a document numbered with made-up credentials, before it is saved. It is
  // kept out of the backend outbox, and once the server has registered its real version, that
  // version updates the receipt it was issued for (orderUuid, or the made-up number it got).
  private async _recordFakeDocument(
    taxRegisterFiscalNumber: string,
    xml: string,
    orderUuid?: string,
  ): Promise<void> {
    const identity = readDocumentIdentity(xml);
    if (!identity.uid) {
      return;
    }
    await this._fakePrro.saveDocument({
      uid: identity.uid,
      taxRegisterFiscalNumber: String(taxRegisterFiscalNumber),
      orderUuid: orderUuid || undefined,
      fiscalNumber: identity.fiscalNumber || undefined,
    });
    this._taxLogger.logTrace('fakePrro: fake document recorded', {
      taxRegister: taxRegisterFiscalNumber,
      uid: identity.uid,
      fiscalNumber: identity.fiscalNumber,
      orderUuid,
    });
  }

  // fakePrro: once the server has registered a fake document's real version, that version goes to
  // the backend — into the outbox and, for a check, onto its receipt (found by the receipt's uuid
  // or the made-up number it got). TaxRegisterWorker delivers it; documents saved real reached
  // the outbox when they were saved.
  private async _deliverRealDocument(
    taxRegisterFiscalNumber: string,
    xml: string,
  ): Promise<void> {
    const uid = readDocumentIdentity(xml).uid;
    const fake = uid ? await this._fakePrro.getDocument(uid) : null;
    if (!fake) {
      return;
    }
    const delivery = planRealDocumentDelivery(
      xml,
      fake,
      taxRegisterFiscalNumber,
    );
    const queued = await this._fakePrro.queueRealDocument(
      delivery.uid,
      delivery.key,
      xml,
      delivery.receipt,
    );
    if (queued) {
      this._taxLogger.logTrace(
        'fakePrro: real document queued for the backend',
        {
          taxRegister: taxRegisterFiscalNumber,
          fiscalNumber: this._getDocumentHead(xml).ORDERTAXNUM,
          madeUpFiscalNumber: fake.fiscalNumber,
          receipt: !!delivery.receipt,
        },
      );
    }
  }

  // Documents the recovery upload does not send: revoked ones go to the trace log, duplicates
  // also to the register's offline errors.
  private async _logRecoverySkips(
    taxRegisterFiscalNumber: string,
    sequence: RecoverySequence,
  ): Promise<void> {
    for (const document of sequence.revoked) {
      this._taxLogger.logTrace('Recovery upload: revoked document not sent', {
        taxRegister: taxRegisterFiscalNumber,
        numLocal: document.NumLocal,
      });
    }
    for (const document of sequence.duplicates) {
      const uid = this._getDocumentHead(document.xmlCheck).UID;
      await this.taxRegisterContextService.saveOfflineSessionError(
        taxRegisterFiscalNumber,
        `Документ ${document.NumLocal} (UID ${uid}) — дублікат: не надсилається і видалений з журналу.`,
        'recoveryValidation',
      );
    }
  }

  // The service checks the document and builds its header from the context: numbering, time,
  // issuer, testing flag and, in an open offline session, the offline fiscal number. cashier: the
  // owner of the validated key (_validateCashierKey).
  private async _buildXmlDoc(
    taxOrder: ITaxOrder,
    taxRegisterFiscalNumber: string,
    cashier: string,
  ): Promise<string> {
    const taxContext = await this.taxRegisterContextService.getTaxContext(
      taxRegisterFiscalNumber,
    );
    const request = {
      docType: taxOrder.documentType,
      docSubType: taxOrder.documentSubType,
      cashier,
      checkTotal: taxOrder.CHECKTOTAL?.toFixed(2),
      testing: taxOrder.testing,
      issuer: this._getIssuerFallback(taxContext),
    };
    // Recovery rules build the header without the document checks and offline limits.
    const docHead = (await this._isRecoveryRegister(taxRegisterFiscalNumber))
      ? await this.taxRegisterContextService.unsafeCreateDocumentHead(
          taxRegisterFiscalNumber,
          request,
        )
      : await this.taxRegisterContextService.createDocumentHead(
          taxRegisterFiscalNumber,
          request,
        );

    taxOrder.uid = docHead.UID;
    taxOrder.orderNumber = docHead.ORDERNUM;
    taxOrder.ORDERDATE = docHead.ORDERDATE;
    taxOrder.ORDERTIME = docHead.ORDERTIME;
    taxOrder.tin = docHead.TIN;
    taxOrder.ipn = docHead.IPN;
    taxOrder.organizationName = docHead.ORGNM;
    taxOrder.pointName = docHead.POINTNM;
    taxOrder.pointAddress = docHead.POINTADDR;
    taxOrder.cashDeskNum = docHead.CASHDESKNUM;
    taxOrder.cashRegisterNum = docHead.CASHREGISTERNUM;
    taxOrder.cashier = docHead.CASHIER;
    taxOrder.ORDERTAXNUM = docHead.ORDERTAXNUM;
    taxOrder.PREVDOCHASH = docHead.PREVDOCHASH;
    taxOrder.offline = docHead.OFFLINE;

    if (docHead.DOCTYPE != 100) {
      taxOrder.testing = docHead.TESTING;
    }

    taxOrder.xmlDocument = this._getTaxObjectXml(taxOrder);
    return taxOrder.xmlDocument;
  }

  // The session end, built by the service with the issuer of the session's start and the owner
  // of the validated key; it is saved only after the server accepts it.
  private async _stopOfflineSessionXml(
    taxRegisterFiscalNumber: string,
  ): Promise<string> {
    const taxConfig = await this.taxRegisterContextService.getTaxConfig(
      taxRegisterFiscalNumber,
    );
    const cashier = await this._validateCashierKey(taxConfig);
    return this.taxRegisterContextService.createOfflineSessionEnd(
      taxRegisterFiscalNumber,
      cashier,
    );
  }

  // Checks the register's cashier key before a document is built: the key is selected and read into
  // the signer (when it is not loaded yet). Returns the owner, the document's CASHIER: the operator
  // whose key signs it.
  private async _validateCashierKey(taxConfig: ITaxConfig): Promise<string> {
    const fiscalNumber = taxConfig?.taxRegister?.fiscalNumber;
    if (!taxConfig?.cashier?.subjectKeyId) {
      throw new Error(`Для ПРРО ${fiscalNumber} не вибрано ключ касира.`);
    }
    const owner = await this._launchPrivateKey(taxConfig).catch((error) => {
      const reason = error?.message || String(error);
      throw new Error(`Ключ касира ПРРО ${fiscalNumber} не зчитано: ${reason}`);
    });
    return this._getCashierName(owner);
  }

  // The first name the key's owner has; a name of only spaces counts as none.
  private _getCashierName(
    owner: AppEndUserOwnerInfo | null | undefined,
  ): string {
    return (
      [owner?.SubjFullName, owner?.SubjCN]
        .map((name) => String(name ?? '').trim())
        .find((name) => !!name) || DEFAULT_CASHIER_NAME
    );
  }

  // The service takes the issuer from the snapshot's TaxObject; the register config fills in
  // only the values TaxObject lacks.
  private _getIssuerFallback(
    taxContext: TaxRegisterContext,
  ): Partial<DocumentIssuer> {
    const taxObject = taxContext.registerState?.TaxObject;
    const config = taxContext.taxConfig;
    const fiscalNumber = String(config?.taxRegister?.fiscalNumber ?? '').trim();
    const registrar = taxObject?.TransactionsRegistrars?.find(
      (item) => String(item?.NumFiscal ?? '').trim() === fiscalNumber,
    );
    return {
      tin: taxObject?.Tin ? undefined : config?.tin,
      ipn: taxObject?.Ipn ? undefined : config?.ipn,
      organizationName: taxObject?.OrgName ? undefined : config?.orgFullName,
      pointName: taxObject?.Name ? undefined : this._getConfigPointName(config),
      pointAddress: taxObject?.Address
        ? undefined
        : config?.taxRegister?.address,
      cashDeskNum: registrar?.NumLocal
        ? undefined
        : config?.taxRegister?.localNumber,
    };
  }

  // The header issuer of the documents a recovery upload sends: the register's as a new
  // document gets it, from the TaxObject in the server's state read for the upload (the
  // context's when the server sent none), the current tax-register config filling in what it
  // lacks.
  private async _getRecoveryIssuer(
    taxRegisterFiscalNumber: string,
    serverState: TransactionsRegistrarStateResponse | null | undefined,
  ): Promise<RecoveryIssuer> {
    const taxContext = await this.taxRegisterContextService.getTaxContext(
      taxRegisterFiscalNumber,
    );
    const config =
      (await this.taxRegisterConfigService
        .getRegisterConfig(taxRegisterFiscalNumber)
        .catch(() => null)) || taxContext?.taxConfig;
    return getRecoveryIssuer(
      taxRegisterFiscalNumber,
      serverState?.TaxObject || taxContext?.registerState?.TaxObject,
      {
        tin: config?.tin,
        ipn: config?.ipn,
        organizationName: config?.orgFullName,
        pointName: this._getConfigPointName(config),
        pointAddress: config?.taxRegister?.address,
        cashDeskNum: config?.taxRegister?.localNumber,
      },
    );
  }

  // The point of sale's name from the register's config. The server sends a register without a
  // linked tax object with no object name, so the store's, the register's or the organization's
  // name stands in.
  private _getConfigPointName(
    config: ITaxConfig | null | undefined,
  ): string | undefined {
    return [
      config?.taxRegister?.objectName,
      config?.store?.name,
      config?.taxRegister?.name,
      config?.orgFullName,
    ]
      .map((value) => String(value ?? '').trim())
      .find((value) => !!value);
  }

  // Recovery rules (unchecked headers and offline starts, and the recovery upload) apply in
  // recovery mode, to a fake context that has documents, and to an offline session numbered with
  // made-up credentials: only the recovery upload can send their documents.
  private async _isRecoveryRegister(
    taxRegisterFiscalNumber: string,
  ): Promise<boolean> {
    if (this._settingsService.taxRecoveryMode) {
      return true;
    }
    const taxContext = await this.taxRegisterContextService.getTaxContext(
      taxRegisterFiscalNumber,
    );
    if (!taxContext) {
      return false;
    }
    const documents = await this.taxRegisterContextService.getDocuments(
      taxRegisterFiscalNumber,
    );
    return needsRecoveryMode(taxContext.registerState, documents);
  }

  // fakePrro: with the tax_fake_prro setting on, a register without a context, whose sale would
  // fail, gets a fake context made up from its tax config at once; no server is asked, so the
  // sale does not wait. A store linked to a context already uses that register. documentType is
  // the operation's document, when it has one.
  private async _ensureStoreFakePrroContext(
    storeId: number,
    documentType?: number,
  ): Promise<void> {
    if (
      this._settingsService.taxFakePrro &&
      !(await this.taxRegisterContextService.getTaxRegisterIdByStoreId(storeId))
    ) {
      await this._ensureFakePrroContext(
        await this.taxRegisterConfigService.getStoreConfig(storeId),
        Number(documentType) !== TaxDocumentType.OPEN_SHIFT,
      );
    }
  }

  private async _ensureRegisterFakePrroContext(
    taxRegisterFiscalNumber: string,
  ): Promise<void> {
    if (this._settingsService.taxFakePrro) {
      await this._ensureFakePrroContext(
        await this.taxRegisterConfigService.getRegisterConfig(
          taxRegisterFiscalNumber,
        ),
      );
    }
  }

  // openShift: whether the fake context opens its shift itself (not when the operation that
  // needs it is a shift opening).
  private async _ensureFakePrroContext(
    config: ITaxConfig | null | undefined,
    openShift = true,
  ): Promise<void> {
    const fiscalNumber = config?.taxRegister?.fiscalNumber;
    if (
      fiscalNumber &&
      !(await this.taxRegisterContextService.hasContext(fiscalNumber))
    ) {
      await this._runRegisterExclusive(fiscalNumber, () =>
        this._createFakePrroContext(config, openShift),
      );
    }
  }

  // The fake context: its state made up from the tax config (buildFakePrroState), then the shift
  // opened in a fake offline session, so the register shows an open shift like any working one.
  // A failure leaves no context, and the next operation tries again.
  private async _createFakePrroContext(
    config: ITaxConfig,
    openShift: boolean,
  ): Promise<void> {
    const fiscalNumber = String(config.taxRegister.fiscalNumber);
    if (await this.taxRegisterContextService.hasContext(fiscalNumber)) {
      return;
    }

    const now = new Date();
    const initialized = await this.taxRegisterContextService.initContext(
      config,
      buildFakePrroState(config.cashier, now),
      null,
      { UID: '', Timestamp: now.toISOString() },
    );
    if (!initialized) {
      throw new Error('Не вдалося створити контекст каси.');
    }
    try {
      await this.taxRegisterContextService.setDisabled(fiscalNumber, false);
      if (openShift) {
        const shiftOpening = new TaxOrder();
        shiftOpening.documentType = 100;
        shiftOpening.testing = false;
        await this._buildXmlDoc(
          shiftOpening,
          fiscalNumber,
          await this._validateCashierKey(config),
        );
        await this._continueOffline(
          fiscalNumber,
          shiftOpening,
          FAKE_PRRO_OFFLINE_CAUSE,
          'fakePrro',
        );
      }
    } catch (error) {
      await this.taxRegisterContextService
        .removeContext(fiscalNumber)
        .catch(() => undefined);
      throw error;
    }
    this._taxLogger.logTrace(
      'fakePrro: register context made up from its config',
      {
        taxRegister: fiscalNumber,
      },
    );
  }

  setTaxApiConnectErrorEmulation(
    taxRegisterFiscalNumber: string,
    enabled: boolean,
  ): void {
    if (enabled) {
      this._pendingTaxApiConnectErrorEmulation.add(taxRegisterFiscalNumber);
      this._activeTaxApiConnectErrorEmulation.delete(taxRegisterFiscalNumber);
      return;
    }

    this._pendingTaxApiConnectErrorEmulation.delete(taxRegisterFiscalNumber);
    this._activeTaxApiConnectErrorEmulation.delete(taxRegisterFiscalNumber);
  }

  isTaxApiConnectErrorEmulationEnabled(
    taxRegisterFiscalNumber: string,
  ): boolean {
    return (
      this._pendingTaxApiConnectErrorEmulation.has(taxRegisterFiscalNumber) ||
      this._activeTaxApiConnectErrorEmulation.has(taxRegisterFiscalNumber)
    );
  }

  private _getTaxObjectXml(taxObject: ITaxOrder): string {
    if (!taxObject) {
      return null;
    }

    if (!taxObject.documentType && taxObject.documentType != 0) {
      const reportXmlObject = TaxDocumentUtility.getZRepo(taxObject);
      return reportXmlObject.xml;
    } else {
      const checkXmlObject = TaxDocumentUtility.getCheck(taxObject);
      return checkXmlObject.xml;
    }
  }

  // ============= TAX REGISER STATE===============

  async getZRepJson(
    taxRegisterFiscalNumber: string,
    zReportFiscalNumber: string,
  ) {
    const config = await this.taxRegisterConfigService.getRegisterConfig(
      taxRegisterFiscalNumber,
    );

    return this._taxApi.ctxGetZRepJson(
      config.cashier.subjectKeyId,
      taxRegisterFiscalNumber,
      zReportFiscalNumber,
    );
  }

  async ctxGetZRepJson(
    cxtKey: string,
    taxRegisterFiscalNumber: string,
    zReportFiscalNumber: string,
  ) {
    return this._taxApi.ctxGetZRepJson(
      cxtKey,
      taxRegisterFiscalNumber,
      zReportFiscalNumber,
    );
  }

  async getDocument(
    taxRegisterFiscalNumber: string,
    document: DocumentItem,
    responseType:
      | 'Availability'
      | 'OriginalXml'
      | 'SignedByServerXml'
      | 'Visualization'
      | 'SignedBySenderXml'
      | 'SignedBySenderAndServerXml' = 'Visualization',
  ) {
    const config =
      (await this.taxRegisterConfigService.getRegisterConfig(
        taxRegisterFiscalNumber,
      )) ||
      (await this.taxRegisterContextService
        .getTaxConfig(taxRegisterFiscalNumber)
        .catch(() => null));
    if (!config?.cashier?.subjectKeyId) {
      throw new Error('Tax register config not found');
    }

    if (document.DocClass == 'Check') {
      return this._taxApi.ctxGetCheckExt(
        config.cashier.subjectKeyId,
        taxRegisterFiscalNumber,
        document.NumFiscal,
        responseType,
      );
    }

    if (document.DocClass == 'ZRep') {
      return this._taxApi.ctxGetZRepExt(
        config.cashier.subjectKeyId,
        taxRegisterFiscalNumber,
        document.NumFiscal,
        responseType,
      );
    }
  }

  // ============= END TAX REGISER STATE===============

  private _displayError(message: string, duration = 20000) {
    if (!message) {
      return;
    }

    this.toastCtrl
      .create({
        message: message,
        duration: duration,
        position: 'top',
        color: 'danger',
        buttons: ['OK'],
      })
      .then((toast) => toast.present());
  }
}
